import { useEffect, useState } from 'preact/hooks';
import { signal, computed } from '@preact/signals';
import { api } from '../api/client';
import { showToast } from '../components/Toast';
import { route } from 'preact-router';
import { SkillBadge } from '../components/skills-center/SkillBadge';
import { useSkillsMeta, enrichSkill } from '../components/skills-center/useSkillsMeta';
import { AdminDockerImagesPage } from './AdminDockerImages';
import { Pagination } from '../components/Pagination';
import { SkeletonBar, Button, Input } from '../components/ui';
import { T } from '../styles/tokens';
// DocsTab moved to standalone /admin/docs page

// ══════════════════════════════════════════════════════════════════════════
// TYPES
// ══════════════════════════════════════════════════════════════════════════

interface Dashboard {
 revenue: { total_usd: number; succeeded: number; failed: number };
 machines: { total: number; running: number; provisioning: number; error: number };
 subscriptions: { total: number; active: number; cancelled: number };
}

interface Plan {
 id: string; name: string; price_usd: number; price_brl: number | null;
 cpus: number; memory_gb: number; storage_gb: number; active: number;
 provider: string; created_at: string;
 description?: string | null; bandwidth_gb?: number; provider_plan_id?: string | null;
 max_agents?: number | null; bonus_credits?: number | null;
}

interface AdminMachine {
 id: string; name: string; status: string; customer_id: string;
 provider: string; ipv4_address: string | null; created_at: string;
 pool_status?: string;
 display_name?: string;
 hostname?: string;
 customer_email?: string;
 runner_connected?: boolean;
 runner_status?: string;
 openclaw_installed?: boolean;
 agent_name?: string;
 skills_count?: number;
 skills_names?: string[];
 installation_stage?: string;
 plan_id?: string | null;
 plan_name?: string | null;
 sub_status?: string | null;
}

interface AdminOrder {
 id: string; customer_id: string; plan_id: string; status: string;
 total_price_usd: number; currency: string; created_at: string;
}

interface SettingView {
 key: string; value: string; is_secret: boolean; category: string;
 label: string | null; description: string | null; updated_at: string;
 has_value: boolean;
}

interface InstallRequest {
 id: string;
 status: 'pending' | 'approved' | 'processing' | 'completed' | 'failed' | 'rejected';
 agent_name: string;
 skills: string[];
 created_at: string;
 retry_count: number;
 installation_stage?: string;
 installation_logs?: Array<{ timestamp: string; message: string } | string>;
 error_message?: string;
 customer_id?: string;
 machine_id?: string;
 llm_provider?: string;
 llm_model?: string;
 brain?: string;
 chat_language?: string;
 use_custom_brain?: boolean;
 customer_email?: string;
 machine_name?: string;
 machine_display_name?: string;
}

interface UnifiedRequest extends InstallRequest {
 type: 'installation' | 'reinstallation';
}

// ══════════════════════════════════════════════════════════════════════════
// STATE
// ══════════════════════════════════════════════════════════════════════════

const dashboard = signal<Dashboard | null>(null);
const plans = signal<Plan[]>([]);
const adminMachines = signal<AdminMachine[]>([]);
const adminOrders = signal<AdminOrder[]>([]);
const settings = signal<SettingView[]>([]);
const grouped = signal<Record<string, SettingView[]>>({});
const loading = signal(true);
const error = signal('');
const successMsg = signal('');
const adminTab = signal<'overview' | 'plans' | 'machines' | 'orders' | 'pedidos' | 'settings' | 'cron' | 'agentes' | 'backups' | 'premium' | 'docker' | 'subscriptions'>('settings');
const showCreatePlan = signal(false);
const editingPlan = signal<Plan | null>(null);
const newPlan = signal({ name: '', description: '', cpus: 2, memory_gb: 4, storage_gb: 50, bandwidth_gb: 10, price_usd: 9.99, price_brl: 49.9, provider: 'hetzner', provider_plan_id: '', max_agents: 5, bonus_credits: 0 });
const creating = signal(false);

// ══════════════════════════════════════════════════════════════════════════
// PAGINATION STATE
// ══════════════════════════════════════════════════════════════════════════

interface PaginationState { page: number; pageSize: number; total: number; totalPages: number; }
const machinesPag = signal<PaginationState>({ page: 1, pageSize: 50, total: 0, totalPages: 0 });
const ordersPag = signal<PaginationState>({ page: 1, pageSize: 50, total: 0, totalPages: 0 });
const customersPag = signal<PaginationState>({ page: 1, pageSize: 50, total: 0, totalPages: 0 });
const subscriptionsPag = signal<PaginationState>({ page: 1, pageSize: 50, total: 0, totalPages: 0 });
const installationsPag = signal<PaginationState>({ page: 1, pageSize: 50, total: 0, totalPages: 0 });
const customersSearch = signal('');

// ══════════════════════════════════════════════════════════════════════════
// PEDIDOS STATE
// ══════════════════════════════════════════════════════════════════════════

const allRequests = signal<UnifiedRequest[]>([]);
const cronEnabled = signal(false);
const cronInterval = signal(5);
const loadingRequests = signal(false);
const pedidosFilter = signal<'all' | 'pending' | 'approved' | 'processing' | 'completed' | 'failed'>('all');
const selectedRequest = signal<UnifiedRequest | null>(null);
const logFilter = signal<string>('all');

const INSTALL_STAGES = [
 { key: 'approved', label: 'Pedido criado', color: T.success, desc: 'Pedido auto-aprovado, aguardando proximo ciclo do cron' },
 { key: 'preparing', label: 'Preparando dados', color: T.warning, desc: 'Gerando pairing token, router token, recuperando brain files, montando script' },
 { key: 'creating_namespace', label: 'Criando Namespace', color: T.primary, desc: 'Namespace K8s isolado para o workspace' },
 { key: 'applying_quota', label: 'Aplicando ResourceQuota', color: T.primary, desc: 'Limites de CPU/RAM conforme plano' },
 { key: 'creating_config', label: 'Criando ConfigMap', color: T.purpleHover, desc: 'Configuracoes do workspace (gateway.yaml, openclaw.json)' },
 { key: 'creating_pvc', label: 'Criando Disco (PVC)', color: T.purpleHover, desc: 'Volume persistente para dados do workspace' },
 { key: 'deploying_pod', label: 'Deployando Pod', color: T.purpleHover, desc: 'Pod com imagem openclaw:latest + sidecar runner' },
 { key: 'configuring_brain', label: 'Instalando cerebro', color: T.purpleHover, desc: 'SOUL.md, MEMORY.md, AGENTS.md — personalidade do agente' },
 { key: 'configuring_llm', label: 'Configurando LLM', color: T.purpleHover, desc: 'Router token + provider customizado + modelos dinamicos' },
 { key: 'creating_service', label: 'Criando Service', color: T.success, desc: 'ClusterIP service para comunicacao interna' },
 { key: 'creating_ingress', label: 'Criando Ingress', color: T.success, desc: 'Ingress com TLS para acesso externo' },
 { key: 'installing_skills', label: 'Instalando Skills', color: T.textSecondary, desc: 'Skills selecionadas — hibrido: servidor local + ClawHub fallback' },
 { key: 'setting_identity', label: 'Definindo identidade', color: T.success, desc: 'Nome do agente + metadados finais' },
 { key: 'saving_access', label: 'Salvando Token e Acesso', color: T.warning, desc: 'Gateway token + endpoint salvos no banco para acesso remoto' },
 { key: 'completed', label: 'Concluido', color: T.success, desc: 'Maquina 100% operacional e online' },
 { key: 'failed', label: 'Falhou', color: T.dangerHover, desc: 'Erro no processo — verifique os logs detalhados' },
];

const stageLabels: Record<string, string> = Object.fromEntries(INSTALL_STAGES.map(s => [s.key, s.label]));

const stageOrder = INSTALL_STAGES.map(s => s.key);
const getStageProgress = (stage?: string) => {
 if (!stage) return 0;
 const index = stageOrder.indexOf(stage);
 return index >= 0 ? ((index + 1) / stageOrder.length) * 100 : 0;
};

/** Extract timestamp for each stage from installation_logs */
function getStageTimestamps(logs?: Array<{ timestamp: string; message: string } | string>): Record<string, string> {
 const map: Record<string, string> = {};
 if (!logs) return map;
 // Map log messages to stage keys by keyword matching
 const stageKeywords: Record<string, string[]> = {
 approved: ['auto-aprovado', 'APPROVED'],
 preparing: ['Preparando', 'pairing token', 'Gerando'],
 creating_namespace: ['Namespace', 'namespace'],
 applying_quota: ['ResourceQuota', 'quota'],
 creating_config: ['ConfigMap', 'config'],
 creating_pvc: ['PVC', 'disco', 'volume'],
 deploying_pod: ['Pod', 'deploy', 'container'],
 configuring_brain: ['cerebro', 'brain', 'SOUL.md'],
 configuring_llm: ['LLM', 'Router token', 'provider'],
 creating_service: ['Service', 'ClusterIP'],
 creating_ingress: ['Ingress', 'TLS'],
 installing_runner: ['Runner', 'runner'],
 ssh_capture: ['SSH', 'ssh', 'RSA'],
 installing_router: ['LLM Router', 'router'],
 installing_skills: ['Skills', 'skills'],
 setting_identity: ['identidade', 'identity', 'agent_name'],
 saving_access: ['Token e Acesso', 'token', 'acesso'],
 completed: ['Concluido', 'COMPLETED', 'operacional'],
 failed: ['FAILED', 'Erro', 'falhou'],
 };
 for (const log of logs) {
 const msg = typeof log === 'string' ? log : log?.message || '';
 const ts = typeof log === 'object' && log?.timestamp ? log.timestamp : '';
 if (!ts) continue;
 for (const [stageKey, keywords] of Object.entries(stageKeywords)) {
 if (map[stageKey]) continue;
 if (keywords.some(kw => msg.toLowerCase().includes(kw.toLowerCase()))) {
 map[stageKey] = ts;
 }
 }
 }
 // Also use first log as 'approved' if not found
 if (!map.approved && logs.length > 0) {
 const first = logs[0];
 const ts = typeof first === 'object' && first?.timestamp ? first.timestamp : '';
 if (ts) map.approved = ts;
 }
 return map;
}

/** Format duration between two ISO timestamps */
function formatDuration(from: string, to: string): string {
 const ms = new Date(to).getTime() - new Date(from).getTime();
 if (ms < 0) return '';
 if (ms < 1000) return `${ms}ms`;
 if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
 if (ms < 3600000) return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
 return `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
}

/** Format time HH:mm:ss from ISO */
function formatTime(iso: string): string {
 return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// ══════════════════════════════════════════════════════════════════════════
// CRON STATE
// ══════════════════════════════════════════════════════════════════════════

interface CronLog {
 id: string;
 cron_name: string;
 started_at: string;
 completed_at: string | null;
 status: 'running' | 'success' | 'partial' | 'skipped' | 'error';
 processed: number;
 succeeded: number;
 failed: number;
 no_machines: number;
 skipped: number;
 details: string[];
 error_message: string | null;
}

const cronLogs = signal<CronLog[]>([]);
const loadingCronLogs = signal(false);
const cronAutoRefresh = signal(true);

async function fetchCronLogs() {
 loadingCronLogs.value = true;
 try {
 const data = await api.get<{ logs: CronLog[]; count: number }>('/cloud/admin/cron/logs');
 cronLogs.value = data.logs;
 } catch (e: any) {
 console.error('Error fetching cron logs:', e);
 } finally {
 loadingCronLogs.value = false;
 }
}

async function fetchPedidos() {
 loadingRequests.value = true;
 try {
 const [installData, reinstallData, cronData] = await Promise.all([
 api.get<{ requests: InstallRequest[] }>('/cloud/admin/installation-requests').catch(() => ({ requests: [] })),
 api.get<{ requests: InstallRequest[] }>('/cloud/admin/reinstallation-requests').catch(() => ({ requests: [] })),
 api.get<{ enabled: boolean }>('/cloud/admin/installation-requests/cron/status').catch(() => ({ enabled: false })),
 ]);

 const unified: UnifiedRequest[] = [
 ...installData.requests.map(r => ({ ...r, type: 'installation' as const })),
 ...reinstallData.requests.map(r => ({ ...r, type: 'reinstallation' as const })),
 ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

 allRequests.value = unified;
 cronEnabled.value = cronData.enabled;
 cronInterval.value = cronData.interval_minutes || 5;
 } catch (e: any) {
 console.error('Error fetching pedidos:', e);
 } finally {
 loadingRequests.value = false;
 }
}

async function handleCronToggle() {
 try {
 if (cronEnabled.value) {
 await api.post('/cloud/admin/installation-requests/cron/stop');
 cronEnabled.value = false;
 } else {
 await api.post('/cloud/admin/installation-requests/cron/start');
 cronEnabled.value = true;
 }
 } catch (e: any) {
 error.value = e.message || 'Erro ao alterar cron';
 }
}

async function handleApproveRequest(req: UnifiedRequest) {
 try {
 const endpoint = req.type === 'reinstallation'
 ? `/cloud/admin/reinstallation-requests/${req.id}`
 : `/cloud/admin/installation-requests/${req.id}`;
 await api.put(endpoint, { action: 'approve' });
 allRequests.value = allRequests.value.map(r =>
 r.id === req.id ? { ...r, status: 'approved' as const } : r
 );
 successMsg.value = ' Pedido aprovado!';
 setTimeout(() => { successMsg.value = ''; }, 3000);
 } catch (e: any) {
 error.value = e.message || 'Erro ao aprovar pedido';
 }
}

async function handleCancelRequest(req: UnifiedRequest) {
 if (!confirm(`Cancelar pedido "${req.agent_name}"?`)) return;
 try {
 const endpoint = req.type === 'reinstallation'
 ? `/cloud/admin/reinstallation-requests/${req.id}`
 : `/cloud/admin/installation-requests/${req.id}`;
 await api.put(endpoint, { action: 'reject', rejected_reason: 'Cancelado pelo admin' });
 allRequests.value = allRequests.value.map(r =>
 r.id === req.id ? { ...r, status: 'rejected' as const } : r
 );
 successMsg.value = ' Pedido cancelado!';
 setTimeout(() => { successMsg.value = ''; }, 3000);
 } catch (e: any) {
 error.value = e.message || 'Erro ao cancelar pedido';
 }
}

// ══════════════════════════════════════════════════════════════════════════
// AGENT CREATION REQUESTS STATE
// ══════════════════════════════════════════════════════════════════════════

const agentRequests = signal<any[]>([]);
const loadingAgentRequests = signal(false);
const agentCreationSettings = signal<{ auto_approve: boolean; cron_enabled: boolean; cron_interval: number }>({ auto_approve: false, cron_enabled: true, cron_interval: 5 });
const agentRequestsFilter = signal<'all' | 'pending' | 'approved' | 'processing' | 'completed' | 'failed'>('all');

async function fetchAgentRequests() {
 loadingAgentRequests.value = true;
 try {
 const [reqData, settingsData] = await Promise.all([
 api.get<{ requests: any[] }>('/cloud/admin/agent-requests').catch(() => ({ requests: [] })),
 api.get<any>('/cloud/admin/agent-creation-settings').catch(() => ({ auto_approve: false, cron_enabled: true, cron_interval: 5 })),
 ]);
 agentRequests.value = reqData.requests || [];
 agentCreationSettings.value = { auto_approve: settingsData.auto_approve, cron_enabled: settingsData.cron_enabled, cron_interval: settingsData.cron_interval };
 } catch (e: any) {
 console.error('Error fetching agent requests:', e);
 } finally {
 loadingAgentRequests.value = false;
 }
}

async function updateAgentCreationSettings(patch: Partial<{ auto_approve: boolean; cron_enabled: boolean; cron_interval: number }>) {
 try {
 await api.put('/cloud/admin/agent-creation-settings', patch);
 agentCreationSettings.value = { ...agentCreationSettings.value, ...patch };
 successMsg.value = ' Configuração salva!';
 setTimeout(() => { successMsg.value = ''; }, 3000);
 } catch (e: any) {
 error.value = e.message || 'Erro ao salvar configuração';
 }
}

async function handleAgentRequestAction(id: string, action: 'approve' | 'reject' | 'cancel') {
 try {
 await api.put(`/cloud/admin/agent-requests/${id}/${action}`);
 const labels: Record<string, string> = { approve: ' Aprovado!', reject: ' Rejeitado!', cancel: ' Cancelado!' };
 successMsg.value = labels[action] || ' OK!';
 setTimeout(() => { successMsg.value = ''; }, 3000);
 fetchAgentRequests();
 } catch (e: any) {
 error.value = e.message || `Erro ao ${action}`;
 }
}

async function processAgentRequests() {
 try {
 const result = await api.post<any>('/cloud/admin/process-agent-requests');
 successMsg.value = ` Processados: ${result.processed || 0}`;
 setTimeout(() => { successMsg.value = ''; }, 3000);
 fetchAgentRequests();
 } catch (e: any) {
 error.value = e.message || 'Erro ao processar';
 }
}

const AGENT_CREATION_STAGES = [
 { key: 'creating_workspace', label: 'Criando workspace', color: T.primary },
 { key: 'writing_brain', label: 'Escrevendo cerebro', color: T.purpleHover },
 { key: 'setting_identity', label: 'Definindo identidade', color: T.success },
 { key: 'configuring_channel', label: 'Configurando canal', color: T.warning },
 { key: 'restarting_gateway', label: 'Reiniciando gateway', color: T.primary },
 { key: 'completed', label: 'Concluido', color: T.success },
];

function AgentRequestsTab() {
 const settings = agentCreationSettings.value;
 const filter = agentRequestsFilter.value;
 const filtered = filter === 'all' ? agentRequests.value : agentRequests.value.filter((r: any) => r.status === filter);
 const statusColors: Record<string, string> = { pending: T.warning, approved: T.success, processing: T.primary, completed: T.success, failed: T.dangerHover, rejected: T.textMuted, cancelled: T.textMuted };
 const statusIcons: Record<string, string> = { pending: '', approved: '', processing: '', completed: '', failed: '', rejected: '', cancelled: '' };

 return (
 <div>
 {/* Settings */}
 <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
 <div style={{ padding: 16, background: T.bgCard, borderRadius: 10, border: `1px solid ${T.border}`, flex: 1, minWidth: 200 }}>
 <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 8 }}>Auto-aprovação</div>
 <Button variant="ghost" onClick={() => updateAgentCreationSettings({ auto_approve: !settings.auto_approve })} style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: settings.auto_approve ? T.success : T.borderHover, color: T.text, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}>
 {settings.auto_approve ? 'ON' : 'OFF'}
 </Button>
 </div>
 <div style={{ padding: 16, background: T.bgCard, borderRadius: 10, border: `1px solid ${T.border}`, flex: 1, minWidth: 200 }}>
 <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 8 }}>Cron de processamento</div>
 <Button variant="ghost" onClick={() => updateAgentCreationSettings({ cron_enabled: !settings.cron_enabled })} style={{ padding: '6px 16px', borderRadius: 8, border: 'none', background: settings.cron_enabled ? T.success : T.borderHover, color: T.text, fontWeight: 600, fontSize: 12, cursor: 'pointer', marginRight: 8 }}>
 {settings.cron_enabled ? 'ON' : 'OFF'}
 </Button>
 <select value={settings.cron_interval} onChange={(e: any) => updateAgentCreationSettings({ cron_interval: parseInt(e.target.value) })} style={{ padding: '6px 10px', borderRadius: 6, background: T.bgOverlay, border: `1px solid ${T.bgActive}`, color: T.text, fontSize: 12 }}>
 {[1, 2, 3, 5, 10, 15, 30, 60].map(v => <option key={v} value={v}>{v} min</option>)}
 </select>
 </div>
 <div style={{ padding: 16, background: T.bgCard, borderRadius: 10, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center' }}>
 <Button variant="primary" size="sm" onClick={processAgentRequests} style={{ background: T.purpleBgStrong }}> Processar Agora</Button>
 </div>
 </div>

 {/* Filter */}
 <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
 {(['all', 'pending', 'approved', 'processing', 'completed', 'failed'] as const).map(f => (
 <Button variant="ghost" key={f} onClick={() => { agentRequestsFilter.value = f; }} style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: filter === f ? T.purpleBg : 'transparent', color: filter === f ? T.primary : T.textSecondary, fontSize: 12, fontWeight: filter === f ? 700 : 500, cursor: 'pointer' }}>
 {f === 'all' ? 'Todos' : f} ({f === 'all' ? agentRequests.value.length : agentRequests.value.filter((r: any) => r.status === f).length})
 </Button>
 ))}
 </div>

 {/* Requests List */}
 {filtered.length === 0 ? (
 <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>Nenhum pedido de criação de agente</div>
 ) : (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
 {filtered.map((r: any) => (
 <div key={r.id} style={{ padding: 16, background: T.bgCard, borderRadius: 10, border: `1px solid ${T.border}` }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
 <div>
 <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{r.agent_name}</span>
 <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 8 }}>{r.model}</span>
 </div>
 <span style={{ fontSize: 11, fontWeight: 600, color: statusColors[r.status] || T.textMuted, padding: '2px 10px', borderRadius: 6, background: `${statusColors[r.status] || T.textMuted}15` }}>
 {statusIcons[r.status] || ''} {r.status}
 </span>
 </div>
 <div style={{ fontSize: 11, color: T.textMuted, display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
 <span>{r.customer_email || r.customer_id}</span>
 <span>{r.machine_name || r.machine_id}</span>
 <span>{r.language}</span>
 <span>{new Date(r.created_at).toLocaleString('pt-BR')}</span>
 {r.retry_count > 0 && <span>Tentativa {r.retry_count}/3</span>}
 </div>
 {r.stage && r.status === 'processing' && (
 <div style={{ marginBottom: 8 }}>
 <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
 {AGENT_CREATION_STAGES.map((s, i) => {
 const stageIdx = AGENT_CREATION_STAGES.findIndex(st => st.key === r.stage);
 const done = i <= stageIdx;
 return <div key={s.key} style={{ flex: 1, height: 4, borderRadius: 2, background: done ? s.color : T.bgInput }} />;
 })}
 </div>
 <div style={{ fontSize: 10, color: T.accent }}>{r.stage_message || r.stage}</div>
 </div>
 )}
 {r.error_message && (
 <div style={{ fontSize: 10, color: T.dangerHover, background: T.dangerBg, padding: '6px 10px', borderRadius: 6, marginBottom: 8 }}>{r.error_message}</div>
 )}
 <div style={{ display: 'flex', gap: 6 }}>
 {r.status === 'pending' && (
 <>
 <Button variant="success" size="xs" onClick={() => handleAgentRequestAction(r.id, 'approve')}> Aprovar</Button>
 <Button variant="danger" size="xs" onClick={() => handleAgentRequestAction(r.id, 'reject')}> Rejeitar</Button>
 </>
 )}
 {(r.status === 'pending' || r.status === 'approved' || r.status === 'failed') && (
 <Button variant="ghost" size="xs" onClick={() => handleAgentRequestAction(r.id, 'cancel')}> Cancelar</Button>
 )}
 </div>
 </div>
 ))}
 </div>
 )}
 </div>
 );
}

// ══════════════════════════════════════════════════════════════════════════
// CEREBROS STATE
// ══════════════════════════════════════════════════════════════════════════

interface AdminCerebro {
 id: string; name: string; slug: string; description: string | null;
 avatar_url: string | null; config_json: string; enabled: number;
 created_at: string; updated_at: string;
 config?: CerebroConfig;
}

interface CerebroConfig {
 agents: string; soul: string; tools: string; identity: string;
 user: string; heartbeat: string; bootstrap: string; memory: string;
}

const CONFIG_FIELDS: Array<{ key: keyof CerebroConfig; label: string; icon: string }> = [
 { key: 'agents', label: 'Agents', icon: '' },
 { key: 'soul', label: 'Soul', icon: '' },
 { key: 'tools', label: 'Tools', icon: '' },
 { key: 'identity', label: 'Identity', icon: '' },
 { key: 'user', label: 'User', icon: '' },
 { key: 'heartbeat', label: 'Heartbeat', icon: '' },
 { key: 'bootstrap', label: 'Bootstrap', icon: '' },
 { key: 'memory', label: 'Memory', icon: '' },
];

const adminCerebros = signal<AdminCerebro[]>([]);
const loadingCerebros = signal(false);
const showCerebroForm = signal(false);
const editingCerebro = signal<AdminCerebro | null>(null);
const cerebroForm = signal<{
 name: string; slug: string; description: string; avatar_url: string; enabled: boolean;
 config: CerebroConfig;
}>({
 name: '', slug: '', description: '', avatar_url: '', enabled: true,
 config: { agents: '', soul: '', tools: '', identity: '', user: '', heartbeat: '', bootstrap: '', memory: '' },
});
const savingCerebro = signal(false);
const activeConfigTab = signal<keyof CerebroConfig>('agents');

async function fetchCerebros() {
 loadingCerebros.value = true;
 try {
 const data = await api.get<{ cerebros: AdminCerebro[] }>('/cloud/admin/cerebros');
 adminCerebros.value = data.cerebros.map(c => ({
 ...c,
 config: typeof c.config_json === 'string' ? JSON.parse(c.config_json) : c.config_json,
 }));
 } catch (e: any) {
 error.value = e.message || 'Erro ao carregar cerebros';
 } finally {
 loadingCerebros.value = false;
 }
}

function openCerebroCreate() {
 editingCerebro.value = null;
 cerebroForm.value = {
 name: '', slug: '', description: '', avatar_url: '', enabled: true,
 config: { agents: '', soul: '', tools: '', identity: '', user: '', heartbeat: '', bootstrap: '', memory: '' },
 };
 activeConfigTab.value = 'agents';
 showCerebroForm.value = true;
}

async function openCerebroEdit(id: string) {
 try {
 const data = await api.get<{ cerebro: AdminCerebro & { config: CerebroConfig } }>(`/cloud/admin/cerebros/${id}`);
 const c = data.cerebro;
 editingCerebro.value = c;
 cerebroForm.value = {
 name: c.name, slug: c.slug, description: c.description || '', avatar_url: c.avatar_url || '',
 enabled: c.enabled === 1,
 config: c.config || { agents: '', soul: '', tools: '', identity: '', user: '', heartbeat: '', bootstrap: '', memory: '' },
 };
 activeConfigTab.value = 'agents';
 showCerebroForm.value = true;
 } catch (e: any) {
 error.value = e.message || 'Erro ao carregar cerebro';
 }
}

async function saveCerebro() {
 savingCerebro.value = true;
 error.value = '';
 try {
 const f = cerebroForm.value;
 if (!f.name || !f.slug) { error.value = 'Nome e slug são obrigatórios'; savingCerebro.value = false; return; }
 if (editingCerebro.value) {
 await api.put(`/cloud/admin/cerebros/${editingCerebro.value.id}`, {
 name: f.name, slug: f.slug, description: f.description, avatar_url: f.avatar_url,
 enabled: f.enabled, config: f.config,
 });
 } else {
 await api.post('/cloud/admin/cerebros', {
 name: f.name, slug: f.slug, description: f.description, avatar_url: f.avatar_url,
 enabled: f.enabled, config: f.config,
 });
 }
 showCerebroForm.value = false;
 fetchCerebros();
 successMsg.value = ` Cerebro ${editingCerebro.value ? 'atualizado' : 'criado'}!`;
 setTimeout(() => { successMsg.value = ''; }, 3000);
 } catch (e: any) {
 error.value = e.message || 'Erro ao salvar cerebro';
 } finally {
 savingCerebro.value = false;
 }
}

async function toggleCerebro(id: string, currentEnabled: number) {
 try {
 await api.put(`/cloud/admin/cerebros/${id}`, { enabled: currentEnabled === 1 ? false : true });
 fetchCerebros();
 } catch (e: any) {
 error.value = e.message || 'Erro ao alterar cerebro';
 }
}

async function deleteCerebroAdmin(id: string) {
 if (!confirm(`Tem certeza que deseja deletar o cerebro ${id}?`)) return;
 try {
 await api.delete(`/cloud/admin/cerebros/${id}`);
 fetchCerebros();
 successMsg.value = ' Cerebro deletado!';
 setTimeout(() => { successMsg.value = ''; }, 3000);
 } catch (e: any) {
 error.value = e.message || 'Erro ao deletar cerebro';
 }
}

function updateConfig(key: keyof CerebroConfig, value: string) {
 cerebroForm.value = {
 ...cerebroForm.value,
 config: { ...cerebroForm.value.config, [key]: value },
 };
}

function autoSlug(name: string): string {
 return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// Provider plans (fetched from real API)
interface ProviderServerType {
 id: string; name: string; cpus: number; memory_gb: number; storage_gb: number;
 storage_type?: string; price_monthly_eur?: number | null; price_monthly_usd?: number | null;
 price_monthly_cents?: number | null; first_period_cents?: number | null;
 bandwidth_tb?: number; network_mbps?: number;
 cpu_type?: string; architecture?: string;
}
interface ProviderLocation { id: string; name: string; city?: string; country?: string; }
const providerPlans = signal<ProviderServerType[]>([]);
const providerLocations = signal<ProviderLocation[]>([]);
const loadingProvider = signal(false);
const providerError = signal('');
const selectedProvider = signal('hetzner');

async function fetchProviderPlans(provider: string) {
 loadingProvider.value = true;
 providerError.value = '';
 providerPlans.value = [];
 providerLocations.value = [];
 try {
 const data = await api.get<any>(`/cloud/admin/provider-plans/${provider}`);
 providerPlans.value = data.catalog || data.server_types || [];
 providerLocations.value = data.locations || [];
 } catch (e: any) {
 providerError.value = e.message || 'Erro ao carregar planos do provider';
 } finally {
 loadingProvider.value = false;
 }
}

function selectServerType(st: ProviderServerType) {
 newPlan.value = {
 ...newPlan.value,
 cpus: st.cpus,
 memory_gb: st.memory_gb,
 storage_gb: st.storage_gb,
 provider_plan_id: st.id,
 description: st.name,
 name: newPlan.value.name || st.name,
 };
}

// Change Customer (reassign machine)
interface CustomerOption { id: string; email: string; name: string | null; status: string; }
const allCustomers = signal<CustomerOption[]>([]);
const showReassign = signal(false);
const reassignMachine = signal<AdminMachine | null>(null);
const reassignCustomerId = signal('');
const reassignSearch = signal('');
const showInstallHistory = signal(false);
const installHistoryMachine = signal<{ id: string; name: string } | null>(null);
const showReinstall = signal(false);
const reinstallMachine = signal<AdminMachine | null>(null);
const reinstallForm = signal<{ agent_name: string; llm_provider: string; llm_model: string; brain: string; chat_language: string; llm_token: string; telegram_token: string }>({
 agent_name: '', llm_provider: 'elon', llm_model: 'claude-haiku-4-5', brain: 'clean', chat_language: 'pt', llm_token: '', telegram_token: '',
});
const reinstallLoading = signal(false);
const reassigning = signal(false);

async function fetchCustomers() {
 try {
 const data = await api.get<{ customers: CustomerOption[] }>('/cloud/admin/customers');
 allCustomers.value = data.customers;
 } catch { /* ignore */ }
}

function openReassign(m: AdminMachine) {
 reassignMachine.value = m;
 reassignCustomerId.value = '';
 reassignSearch.value = '';
 showReassign.value = true;
 if (allCustomers.value.length === 0) fetchCustomers();
}

async function doReassign() {
 if (!reassignMachine.value || !reassignCustomerId.value) return;
 reassigning.value = true;
 error.value = '';
 try {
 await api.post(`/cloud/admin/machines/${reassignMachine.value.id}/change-customer`, {
 new_customer_id: reassignCustomerId.value,
 });
 showReassign.value = false;
 successMsg.value = ' Máquina reatribuída com sucesso!';
 setTimeout(() => { successMsg.value = ''; }, 3000);
 fetchAll();
 } catch (e: any) {
 error.value = e.message || 'Erro ao reatribuir máquina';
 } finally {
 reassigning.value = false;
 }
}

const saving = signal(false);
const testing = signal('');
const testResult = signal<{ category: string; ok: boolean; message: string } | null>(null);

const editValues = signal<Record<string, string>>({});
const dirtyKeys = computed(() => Object.keys(editValues.value));

// ══════════════════════════════════════════════════════════════════════════
// API CALLS
// ══════════════════════════════════════════════════════════════════════════

async function fetchAll() {
 loading.value = true;
 error.value = '';
 try {
 const mp = machinesPag.value;
 const op = ordersPag.value;
 const [d, p, m, o, s] = await Promise.all([
 api.get<{ dashboard: Dashboard }>('/cloud/admin/dashboard').catch(() => ({ dashboard: null })),
 api.get<{ plans: Plan[] }>('/cloud/admin/plans').catch(() => ({ plans: [] })),
 api.get<any>(`/cloud/admin/machines?page=${mp.page}&pageSize=${mp.pageSize}`).catch(() => ({ machines: [], pagination: null })),
 api.get<any>(`/cloud/admin/orders?page=${op.page}&pageSize=${op.pageSize}`).catch(() => ({ orders: [], pagination: null })),
 api.get<{ settings: SettingView[]; grouped: Record<string, SettingView[]> }>('/cloud/admin/settings').catch(() => ({ settings: [], grouped: {} })),
 ]);
 dashboard.value = d.dashboard;
 plans.value = p.plans;
 adminMachines.value = m.machines;
 if (m.pagination) machinesPag.value = { ...machinesPag.value, ...m.pagination };
 adminOrders.value = o.orders;
 if (o.pagination) ordersPag.value = { ...ordersPag.value, ...o.pagination };
 settings.value = s.settings;
 grouped.value = s.grouped;
 editValues.value = {};
 } catch (e: any) {
 error.value = e.message || 'Erro ao carregar admin';
 } finally {
 loading.value = false;
 }
}

async function returnToPool(machineId: string) {
 try {
 await api.put(`/cloud/admin/machines/${machineId}/return-to-pool`, {});
 fetchAll();
 successMsg.value = 'Devolvida ao pool com sucesso';
 setTimeout(() => { successMsg.value = ''; }, 3000);
 } catch (e: any) {
 error.value = e.message;
 }
}

async function changePoolStatus(machineId: string, newStatus: string) {
 try {
 await api.put(`/cloud/admin/machines/${machineId}/pool-status`, { pool_status: newStatus });
 fetchAll();
 successMsg.value = ` Pool status alterado para ${newStatus}`;
 setTimeout(() => { successMsg.value = ''; }, 3000);
 } catch (e: any) {
 error.value = e.message;
 }
}

async function saveSettings() {
 if (Object.keys(editValues.value).length === 0) return;
 saving.value = true;
 error.value = '';
 successMsg.value = '';
 try {
 const data = await api.put<{
 updated: string[]; errors: string[];
 settings: SettingView[]; grouped: Record<string, SettingView[]>;
 }>('/cloud/admin/settings', { settings: editValues.value });

 settings.value = data.settings;
 grouped.value = data.grouped;
 editValues.value = {};
 successMsg.value = ` ${data.updated.length} configurações salvas!`;
 if (data.errors.length > 0) {
 error.value = data.errors.join(', ');
 }
 setTimeout(() => { successMsg.value = ''; }, 3000);
 } catch (e: any) {
 error.value = e.message || 'Erro ao salvar';
 } finally {
 saving.value = false;
 }
}

async function testConnection(category: string) {
 testing.value = category;
 testResult.value = null;
 try {
 const result = await api.post<{ ok: boolean; message: string }>('/cloud/admin/settings/test', { category });
 testResult.value = { category, ...result };
 } catch (e: any) {
 testResult.value = { category, ok: false, message: e.message };
 } finally {
 testing.value = '';
 }
}

function openPlanEdit(plan: Plan) {
 editingPlan.value = plan;
 newPlan.value = {
 name: plan.name,
 description: plan.description || '',
 cpus: plan.cpus,
 memory_gb: plan.memory_gb,
 storage_gb: plan.storage_gb,
 bandwidth_gb: plan.bandwidth_gb || 10,
 price_usd: plan.price_usd,
 price_brl: plan.price_brl || 0,
 provider: plan.provider,
 provider_plan_id: plan.provider_plan_id || '',
 max_agents: plan.max_agents || 5,
 bonus_credits: plan.bonus_credits || 0,
 };
 showCreatePlan.value = true;
}

function closePlanForm() {
 showCreatePlan.value = false;
 editingPlan.value = null;
 providerPlans.value = [];
 providerLocations.value = [];
 newPlan.value = { name: '', description: '', cpus: 2, memory_gb: 4, storage_gb: 50, bandwidth_gb: 10, price_usd: 9.99, price_brl: 49.9, provider: 'hetzner', provider_plan_id: '', max_agents: 5, bonus_credits: 0 };
}

async function createPlan() {
 creating.value = true;
 try {
 const p = newPlan.value;
 const payload = {
 name: p.name, description: p.description, provider: p.provider,
 cpus: p.cpus, memory_gb: p.memory_gb, storage_gb: p.storage_gb,
 bandwidth_gb: p.bandwidth_gb, price_usd: p.price_usd, price_brl: p.price_brl,
 provider_plan_id: p.provider_plan_id || null,
 max_agents: p.max_agents || null,
 bonus_credits: p.bonus_credits || null,
 };
 if (editingPlan.value) {
 await api.patch(`/cloud/admin/plans/${editingPlan.value.id}`, payload);
 } else {
 await api.post('/cloud/admin/plans', payload);
 }
 closePlanForm();
 fetchAll();
 } catch (e: any) {
 error.value = e.message;
 } finally {
 creating.value = false;
 }
}

async function togglePlan(planId: string, active: number) {
 try {
 await api.patch(`/cloud/admin/plans/${planId}`, { active: active ? 0 : 1 });
 fetchAll();
 } catch (e: any) {
 error.value = e.message;
 }
}

// ══════════════════════════════════════════════════════════════════════════
// HELPERS
// ══════════════════════════════════════════════════════════════════════════

function formatDate(iso: string): string {
 return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function setEdit(key: string, value: string) {
 editValues.value = { ...editValues.value, [key]: value };
}

function getEditOrCurrent(s: SettingView): string {
 if (s.key in editValues.value) return editValues.value[s.key];
 return s.is_secret ? '' : s.value;
}

// ══════════════════════════════════════════════════════════════════════════
// CATEGORY CONFIGS
// ══════════════════════════════════════════════════════════════════════════

const CATEGORY_META: Record<string, { icon: string; title: string; color: string; testable: boolean }> = {
 stripe: { icon: '', title: 'Stripe', color: '#635BFF', testable: true },
 pagarme: { icon: '', title: 'Pagar.me', color: T.success, testable: false },
 hetzner: { icon: '', title: 'Hetzner Cloud', color: '#D50C2D', testable: true },
 k8s: { icon: '', title: 'Kubernetes (DOKS)', color: '#326CE5', testable: true },
 features: { icon: '', title: 'Feature Flags', color: T.warning, testable: false },
 general: { icon: '', title: 'Geral', color: T.textMuted, testable: false },
};

// ══════════════════════════════════════════════════════════════════════════
// COMPONENTS
// ══════════════════════════════════════════════════════════════════════════

function StatCard({ icon, label, value, color }: { icon: string; label: string; value: string | number; color: string }) {
 return (
 <div style={{
 background: T.bgInput, border: `1px solid ${T.border}`,
 borderRadius: 14, padding: 20, flex: 1, minWidth: 140,
 }}>
 <div style={{ fontSize: 24, marginBottom: 8 }}>{icon}</div>
 <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
 <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{label}</div>
 </div>
 );
}

function SettingInput({ s }: { s: SettingView }) {
 const currentValue = getEditOrCurrent(s);
 const isDirty = s.key in editValues.value;
 const isBool = s.value === 'true' || s.value === 'false' || s.key.endsWith('_enabled') || s.key === 'cloud_enabled' || s.key === 'auto_provision' || s.key === 'webhook_validation';

 if (isBool && !s.is_secret) {
 const isOn = (isDirty ? editValues.value[s.key] : s.value) === 'true';
 return (
 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
 <div style={{ flex: 1 }}>
 <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{s.label || s.key}</div>
 <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{s.description}</div>
 </div>
 <div
 onClick={() => setEdit(s.key, isOn ? 'false' : 'true')}
 style={{
 width: 48, height: 26, borderRadius: 13, cursor: 'pointer',
 background: isOn ? 'rgba(52,199,89,0.8)' : T.borderHover,
 position: 'relative', transition: 'background 0.2s', flexShrink: 0,
 border: isDirty ? '2px solid #FF9500' : '2px solid transparent',
 }}
 >
 <div style={{
 width: 20, height: 20, borderRadius: '50%', background: '#fff',
 position: 'absolute', top: 1, left: isOn ? 24 : 2,
 transition: 'left 0.2s', boxShadow: `0 1px 3px ${T.shadowSm}`,
 }} />
 </div>
 </div>
 );
 }

 const SELECT_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
 stripe_mode: [{ value: 'test', label: ' Test (Sandbox)' }, { value: 'live', label: ' Live (Produção)' }],
 pagarme_mode: [{ value: 'test', label: ' Test (Sandbox)' }, { value: 'live', label: ' Live (Produção)' }],
 default_provider: [{ value: 'k8s', label: 'Kubernetes (DOKS)' }, { value: 'hetzner', label: ' Hetzner Cloud' }],
 hetzner_default_location: [
 { value: 'fsn1', label: '🇩🇪 Falkenstein (fsn1)' }, { value: 'nbg1', label: '🇩🇪 Nuremberg (nbg1)' },
 { value: 'hel1', label: '🇫🇮 Helsinki (hel1)' }, { value: 'ash', label: '🇺🇸 Ashburn (ash)' },
 { value: 'hil', label: '🇺🇸 Hillsboro (hil)' }, { value: 'sin', label: '🇸🇬 Singapore (sin)' },
 ],
 hetzner_default_image: [
 { value: 'ubuntu-22.04', label: 'Ubuntu 22.04 LTS' }, { value: 'ubuntu-24.04', label: 'Ubuntu 24.04 LTS' },
 { value: 'debian-12', label: 'Debian 12' }, { value: 'debian-11', label: 'Debian 11' },
 { value: 'rocky-9', label: 'Rocky Linux 9' }, { value: 'fedora-39', label: 'Fedora 39' },
 ],
 stripe_currency: [{ value: 'usd', label: '🇺🇸 USD (Dólar)' }],
 };

 if (SELECT_OPTIONS[s.key]) {
 const options = SELECT_OPTIONS[s.key];
 return (
 <div>
 <label style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, display: 'block', marginBottom: 4 }}>
 {s.label || s.key}
 {isDirty && <span style={{ color: T.warning, marginLeft: 6 }}> modificado</span>}
 </label>
 <select
 value={isDirty ? editValues.value[s.key] : s.value}
 onChange={(e: any) => setEdit(s.key, e.target.value)}
 style={{
 width: '100%', padding: '10px 14px', borderRadius: 10,
 border: isDirty ? '2px solid #FF9500' : `1px solid ${T.bgActive}`,
 background: T.bgCard, color: T.text, fontSize: 14,
 appearance: 'none', WebkitAppearance: 'none',
 backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%236B7A90\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
 backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center',
 }}
 >
 {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
 </select>
 <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{s.description}</div>
 </div>
 );
 }

 return (
 <div>
 <label style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, display: 'block', marginBottom: 4 }}>
 {s.label || s.key}
 {s.is_secret && <span style={{ marginLeft: 6, fontSize: 10, color: T.warning }}> encriptado</span>}
 {isDirty && <span style={{ color: T.warning, marginLeft: 6 }}> modificado</span>}
 </label>
 <input
 type={s.is_secret ? 'password' : 'text'}
 placeholder={s.is_secret ? (s.has_value ? '•••••••• (já configurado, deixe vazio para manter)' : 'Não configurado') : ''}
 value={currentValue}
 onInput={(e: any) => setEdit(s.key, e.target.value)}
 style={{
 width: '100%', padding: '10px 14px', borderRadius: 10,
 border: isDirty ? '2px solid #FF9500' : `1px solid ${T.bgActive}`,
 background: T.bgCard, color: T.text, fontSize: 14, outline: 'none',
 fontFamily: s.is_secret ? 'monospace' : 'inherit',
 }}
 />
 <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{s.description}</div>
 </div>
 );
}

function SettingsCategory({ category, items }: { category: string; items: SettingView[] }) {
 const meta = CATEGORY_META[category] || { icon: '', title: category, color: T.textMuted, testable: false };

 return (
 <div style={{
 background: T.bgInput, border: `1px solid ${T.border}`,
 borderRadius: 16, padding: 24, marginBottom: 20,
 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
 <div style={{ width: 10, height: 10, borderRadius: '50%', background: meta.color, flexShrink: 0 }} />
 <div>
 <div style={{ fontSize: 18, fontWeight: 800, color: meta.color }}>{meta.title}</div>
 <div style={{ fontSize: 12, color: T.textMuted }}>{items.length} configurações</div>
 </div>
 </div>
 {meta.testable && (
 <Button
 variant="secondary"
 size="sm"
 onClick={() => testConnection(category)}
 disabled={testing.value === category}
 style={{ background: T.purpleBg, color: T.purpleHover }}
 >
 {testing.value === category ? ' Testando...' : ' Testar Conexão'}
 </Button>
 )}
 </div>

 {testResult.value && testResult.value.category === category && (
 <div style={{
 padding: '10px 16px', borderRadius: 10, marginBottom: 16, fontSize: 13,
 background: testResult.value.ok ? T.successBg : T.dangerBg,
 border: `1px solid ${testResult.value.ok ? T.successBg : T.dangerBg}`,
 color: testResult.value.ok ? T.success : T.danger,
 }}>
 {testResult.value.ok ? '' : ''} {testResult.value.message}
 </div>
 )}

 <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
 {items.map(s => <SettingInput key={s.key} s={s} />)}
 </div>
 </div>
 );
}

function InputField({ label, value, onChange, type = 'text' }: { label: string; value: any; onChange: (v: any) => void; type?: string }) {
 return <Input label={label} value={value ?? ''} onChange={(v: string) => onChange(type === 'number' ? Number(v) : v)} type={type} />;
}

// ══════════════════════════════════════════════════════════════════════════
// PEDIDOS TAB COMPONENT
// ══════════════════════════════════════════════════════════════════════════

function PedidosTab() {
 const statusColors: Record<string, string> = {
 pending: T.warning, approved: T.success, processing: T.primary, completed: T.success, failed: T.dangerHover, rejected: T.textSecondary,
 };

 const filtered = allRequests.value.filter(r =>
 pedidosFilter.value === 'all' || r.status === pedidosFilter.value
 );

 const counts = {
 all: allRequests.value.length,
 pending: allRequests.value.filter(r => r.status === 'pending').length,
 approved: allRequests.value.filter(r => r.status === 'approved').length,
 processing: allRequests.value.filter(r => r.status === 'processing').length,
 completed: allRequests.value.filter(r => r.status === 'completed').length,
 failed: allRequests.value.filter(r => r.status === 'failed').length,
 };

 return (
 <div>
 {/* Cron Control */}
 <div style={{
 padding: 16, background: T.purpleBg, borderRadius: 12,
 border: `1px solid ${T.primaryBg}`, display: 'flex',
 alignItems: 'center', justifyContent: 'space-between', marginBottom: 20,
 }}>
 <div>
 <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>
 {cronEnabled.value ? 'Cron Ativo' : 'Cron Desativado'}
 </div>
 <div style={{ fontSize: 12, color: T.textSecondary }}>
 {cronEnabled.value ? 'Processando pedidos automaticamente' : 'Nenhum processamento automatico'}
 </div>
 </div>
 <Button variant="ghost" onClick={handleCronToggle} style={{
 padding: '8px 16px', borderRadius: 8, border: '1px solid #334155',
 background: cronEnabled.value ? T.dangerBg : T.successBg,
 color: cronEnabled.value ? T.danger : T.success, fontSize: 12, fontWeight: 600, cursor: 'pointer',
 }}>
 {cronEnabled.value ? 'Desativar' : 'Ativar'}
 </Button>
 </div>

 {/* Filters */}
 <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
 {([
 ['all', 'Todos'],
 ['pending', 'Pendentes'],
 ['approved', 'Aprovados'],
 ['processing', 'Processando'],
 ['completed', 'Concluidos'],
 ['failed', 'Falhos'],
 ] as const).map(([key, label]) => (
 <Button variant="ghost"
 key={key}
 onClick={() => pedidosFilter.value = key}
 style={{
 padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
 border: pedidosFilter.value === key ? `1px solid ${T.primary}` : `1px solid ${T.bgActive}`,
 background: pedidosFilter.value === key ? T.purpleBg : 'transparent',
 color: pedidosFilter.value === key ? T.primary : T.textSecondary,
 }}
 >
 {label} ({counts[key]})
 </Button>
 ))}
 <Button variant="ghost" onClick={() => fetchPedidos()} style={{
 padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
 border: `1px solid ${T.bgActive}`, background: 'transparent', color: T.textSecondary,
 marginLeft: 'auto',
 }}>
 Atualizar
 </Button>
 </div>

 {/* Requests List */}
 {loadingRequests.value ? (
 <div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>
 ) : filtered.length === 0 ? (
 <div style={{ textAlign: 'center', padding: 48, color: T.textMuted }}>
 Nenhum pedido {pedidosFilter.value !== 'all' ? `com status "${pedidosFilter.value}"` : 'encontrado'}.
 </div>
 ) : (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
 {filtered.map(r => {
 const progress = getStageProgress(r.installation_stage);
 const sc = statusColors[r.status] || T.textSecondary;

 return (
 <div key={`${r.type}-${r.id}`} style={{
 background: T.bgInput, border: '1px solid #334155',
 borderRadius: 10, padding: 16, cursor: 'pointer',
 transition: 'border-color 0.15s',
 }} onClick={() => selectedRequest.value = r}
 onMouseEnter={(e: any) => e.currentTarget.style.borderColor = T.purpleHover}
 onMouseLeave={(e: any) => e.currentTarget.style.borderColor = T.textPrimary}
 >
 {/* Header */}
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{
 padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, letterSpacing: '0.5px',
 background: r.type === 'installation' ? T.successBg : T.purpleBg,
 color: r.type === 'installation' ? T.success : T.purpleHover,
 border: `1px solid ${r.type === 'installation' ? T.successBg : T.purpleBg}`,
 }}>
 {r.type === 'installation' ? 'INSTALL' : 'REINSTALL'}
 </span>
 <span style={{ fontSize: 14, fontWeight: 600, color: T.text }}>
 {r.agent_name}
 </span>
 {(r.machine_display_name || r.machine_name) && (
 <span style={{ fontSize: 12, color: T.textSecondary, fontFamily: 'monospace' }}>
 {r.machine_display_name || r.machine_name}
 </span>
 )}
 </div>
 <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
 <div style={{ width: 6, height: 6, borderRadius: '50%', background: sc }} />
 <span style={{ fontSize: 11, fontWeight: 600, color: sc }}>
 {r.status.toUpperCase()}
 </span>
 </div>
 </div>

 {/* Customer */}
 <div style={{ fontSize: 12, color: T.textSecondary, marginBottom: 8, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
 {r.customer_email && (
 <span> {r.customer_email}</span>
 )}
 </div>

 {/* Details grid */}
 <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
 {r.llm_provider && (
 <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: T.bgInput, color: T.textSecondary }}>
 {r.llm_provider}
 </span>
 )}
 {r.llm_model && (
 <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: T.bgInput, color: T.textSecondary }}>
 {r.llm_model}
 </span>
 )}
 {r.brain && (
 <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: T.bgInput, color: T.textSecondary }}>
 Brain: {r.brain}
 </span>
 )}
 {r.chat_language && (
 <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: T.bgInput, color: T.textSecondary }}>
 {r.chat_language === 'pt' ? 'Português' : r.chat_language === 'en' ? 'English' : r.chat_language}
 </span>
 )}
 {r.machine_id && (
 <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, background: T.bgInput, color: T.textSecondary }}>
 {r.machine_id.slice(0, 10)}...
 </span>
 )}
 </div>

 {/* Skills */}
 {r.skills && r.skills.length > 0 && (
 <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
 {r.skills.map(s => (
 <span key={s} style={{
 fontSize: 10, padding: '2px 8px', borderRadius: 10,
 background: T.purpleBg, color: T.purpleHover, fontWeight: 600,
 }}>
 {s}
 </span>
 ))}
 </div>
 )}

 {/* Meta */}
 <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>
 ID: {r.id.slice(0, 8)}... • {formatDate(r.created_at)} • Tentativas: {r.retry_count}/3
 </div>

 {/* Progress bar */}
 {r.installation_stage && (
 <div style={{ marginBottom: 8 }}>
 <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 4 }}>
 Etapa: {stageLabels[r.installation_stage] || r.installation_stage}
 </div>
 <div style={{ width: '100%', height: 4, background: T.bgInput, borderRadius: 2 }}>
 <div style={{ height: '100%', width: `${progress}%`, background: sc, borderRadius: 2, transition: 'width 0.3s' }} />
 </div>
 </div>
 )}

 {/* Error */}
 {r.error_message && (
 <div style={{
 fontSize: 11, color: T.dangerHover, background: T.dangerBg,
 padding: '6px 10px', borderRadius: 6, marginBottom: 8, fontFamily: 'monospace',
 }}>
 {r.error_message}
 </div>
 )}

 {/* Actions */}
 <div style={{ display: 'flex', gap: 8 }}>
 {r.status === 'pending' && (
 <Button variant="success" size="xs" onClick={(e: any) => { e.stopPropagation(); handleApproveRequest(r); }}> Aprovar</Button>
 )}
 {(r.status === 'pending' || r.status === 'processing') && (
 <Button variant="danger" size="xs" onClick={(e: any) => { e.stopPropagation(); handleCancelRequest(r); }}> Cancelar</Button>
 )}
 <Button variant="ghost" size="xs" onClick={(e: any) => { e.stopPropagation(); selectedRequest.value = r; }}> Detalhes</Button>
 {r.machine_id && (
 <a href={`/cloud/machines/${r.machine_id}`} onClick={(e: any) => e.stopPropagation()} style={{
 padding: '6px 14px', borderRadius: 6, border: `1px solid ${T.successBg}`,
 background: T.successBg, color: T.success, fontSize: 11, fontWeight: 600, cursor: 'pointer',
 textDecoration: 'none', display: 'inline-block',
 }}>
 Dashboard
 </a>
 )}
 </div>
 </div>
 );
 })}
 </div>
 )}

 {/* Detail Modal */}
 {selectedRequest.value && (
 <div
 onClick={() => selectedRequest.value = null}
 style={{
 position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
 background: T.bgOverlay, display: 'flex', alignItems: 'center',
 justifyContent: 'center', zIndex: 1000,
 }}
 >
 <div onClick={(e: any) => e.stopPropagation()} style={{
 background: '#0f172a', border: '1px solid #334155',
 borderRadius: 14, padding: 24, maxWidth: 620, width: '90%', maxHeight: '80vh', overflowY: 'auto',
 }}>
 {/* Header */}
 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
 <div>
 <div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 4 }}>{selectedRequest.value.agent_name}</div>
 <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{
 padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, letterSpacing: '0.5px',
 background: selectedRequest.value.type === 'installation' ? T.successBg : T.purpleBg,
 color: selectedRequest.value.type === 'installation' ? T.success : T.purpleHover,
 border: `1px solid ${selectedRequest.value.type === 'installation' ? T.successBg : T.purpleBg}`,
 }}>
 {selectedRequest.value.type === 'installation' ? 'INSTALL' : 'REINSTALL'}
 </span>
 <span style={{ fontSize: 11, color: T.textMuted }}>{formatDate(selectedRequest.value.created_at)}</span>
 </div>
 </div>
 <Button variant="ghost" onClick={() => selectedRequest.value = null} style={{
 width: 28, height: 28, borderRadius: 6, border: '1px solid #334155', background: T.bgInput,
 color: T.textSecondary, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
 }}>x</Button>
 </div>

 {/* Status */}
 <div style={{
 display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, padding: '10px 14px',
 borderRadius: 8, background: T.bgInput, border: '1px solid #334155',
 }}>
 <div style={{
 width: 8, height: 8, borderRadius: '50%',
 background: selectedRequest.value.status === 'completed' ? T.success : selectedRequest.value.status === 'failed' ? T.dangerHover : selectedRequest.value.status === 'processing' ? T.purpleHover : T.warning,
 }} />
 <span style={{ fontSize: 13, fontWeight: 600, color: T.text, textTransform: 'capitalize' }}>
 {selectedRequest.value.status === 'completed' ? 'Concluido' : selectedRequest.value.status === 'failed' ? 'Falhou' : selectedRequest.value.status === 'processing' ? 'Processando' : 'Pendente'}
 </span>
 {selectedRequest.value.retry_count > 0 && (
 <span style={{ fontSize: 10, color: T.textMuted, marginLeft: 'auto' }}>Tentativa {selectedRequest.value.retry_count}/3</span>
 )}
 </div>

 {/* Configuration */}
 <div style={{ marginBottom: 20 }}>
 <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 10, borderBottom: '1px solid #1e293b', paddingBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Configuracao</div>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
 <div style={{ fontSize: 12, color: T.textMuted }}>Provedor <span style={{ color: T.text, fontWeight: 600, display: 'block' }}>{selectedRequest.value.llm_provider || 'router'}</span></div>
 <div style={{ fontSize: 12, color: T.textMuted }}>Modelo <span style={{ color: T.text, fontWeight: 600, display: 'block' }}>{selectedRequest.value.llm_model || '—'}</span></div>
 <div style={{ fontSize: 12, color: T.textMuted }}>Brain <span style={{ color: T.text, fontWeight: 600, display: 'block' }}>{selectedRequest.value.brain || 'clean'}</span></div>
 <div style={{ fontSize: 12, color: T.textMuted }}>Idioma <span style={{ color: T.text, fontWeight: 600, display: 'block' }}>{selectedRequest.value.chat_language === 'pt' ? 'Portugues' : selectedRequest.value.chat_language || '—'}</span></div>
 {selectedRequest.value.customer_email && (
 <div style={{ fontSize: 12, color: T.textMuted }}>Cliente <span style={{ color: T.text, fontWeight: 600, display: 'block' }}>{selectedRequest.value.customer_email}</span></div>
 )}
 {(selectedRequest.value.machine_name || selectedRequest.value.machine_display_name) && (
 <div style={{ fontSize: 12, color: T.textMuted }}>Maquina <span style={{ color: T.text, fontWeight: 600, display: 'block' }}>{selectedRequest.value.machine_display_name || selectedRequest.value.machine_name}</span></div>
 )}
 </div>
 </div>

 {/* Stage Timeline */}
 <div style={{ marginBottom: 20 }}>
 <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 12, borderBottom: '1px solid #334155', paddingBottom: 6 }}>Etapas do Processo</div>
 {(() => {
 const stageTs = getStageTimestamps(selectedRequest.value?.installation_logs);
 return INSTALL_STAGES.map((stage, idx) => {
 const currentIdx = INSTALL_STAGES.findIndex(s => s.key === (selectedRequest.value?.installation_stage || 'pending'));
 const isCompleted = currentIdx > idx;
 const isCurrent = currentIdx === idx;
 const isFuture = currentIdx < idx;
 const isFailed = selectedRequest.value?.status === 'failed' && isCurrent;

 if (stage.key === 'completed' && selectedRequest.value?.status !== 'completed' && !isCompleted && !isCurrent) return null;
 if (stage.key === 'failed' && selectedRequest.value?.status !== 'failed') return null;

 const ts = stageTs[stage.key];
 const prevStage = idx > 0 ? INSTALL_STAGES[idx - 1] : null;
 const prevTs = prevStage ? stageTs[prevStage.key] : null;
 const dur = ts && prevTs ? formatDuration(prevTs, ts) : '';

 const dotColor = isCompleted ? T.success : isCurrent ? (isFailed ? T.dangerHover : T.purpleHover) : T.textPrimary;
 const dotBg = isCompleted ? T.successBg : isCurrent ? (isFailed ? T.dangerBg : T.purpleBg) : T.bgCard;

 return (
 <div key={stage.key} style={{
 display: 'flex', alignItems: 'flex-start', gap: 12, padding: '6px 0',
 opacity: isFuture ? 0.35 : 1,
 }}>
 {/* Timeline dot + line */}
 <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
 <div style={{
 width: 10, height: 10, borderRadius: '50%',
 background: dotBg, border: `2px solid ${dotColor}`,
 marginTop: 3,
 }} />
 {idx < INSTALL_STAGES.length - 1 && (
 <div style={{ width: 1, height: 24, background: isCompleted ? T.successBg : T.bgInput, marginTop: 2 }} />
 )}
 </div>
 <div style={{ flex: 1, minWidth: 0 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{
 fontSize: 12, fontWeight: isCompleted || isCurrent ? 600 : 400,
 color: isCompleted ? T.success : isCurrent ? (isFailed ? T.dangerHover : T.purpleHover) : T.textSecondary,
 }}>
 {stage.label}
 </span>
 {ts && (isCompleted || isCurrent) && (
 <span style={{ fontSize: 10, color: T.textMuted, fontFamily: 'monospace' }}>{formatTime(ts)}</span>
 )}
 {dur && isCompleted && (
 <span style={{ fontSize: 9, color: T.purpleHover, background: T.purpleBg, padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>{dur}</span>
 )}
 {isCurrent && !isFailed && (
 <span style={{ fontSize: 9, color: T.purpleHover, fontWeight: 600 }}>em andamento</span>
 )}
 {isFailed && (
 <span style={{ fontSize: 9, color: T.dangerHover, fontWeight: 600 }}>falhou</span>
 )}
 </div>
 {(isCompleted || isCurrent) && (
 <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1, lineHeight: '1.3' }}>{stage.desc}</div>
 )}
 </div>
 </div>
 );
 });
 })()}
 </div>

 {/* Skills */}
 <div style={{ marginBottom: 20 }}>
 <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 10, borderBottom: '1px solid #1e293b', paddingBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Skills ({selectedRequest.value.skills.length})</div>
 <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
 {selectedRequest.value.skills.length > 0 ? (
 selectedRequest.value.skills.map((skill: string) => {
 const sm = enrichSkill(skill, skillsMeta);
 return (
 <span key={skill} style={{
 padding: '4px 10px', borderRadius: 6, background: T.purpleBg,
 color: T.purpleHover, fontSize: 10, fontWeight: 600,
 display: 'inline-flex', alignItems: 'center', gap: 4,
 }}>
 {skill}
 <SkillBadge tier={sm.tier} badge={sm.badge} size="sm" />
 </span>
 );
 })
 ) : (
 <span style={{ color: T.textMuted, fontSize: 11 }}>Nenhuma skill</span>
 )}
 </div>
 </div>

                {/* Logs */}
                {selectedRequest.value.installation_logs && selectedRequest.value.installation_logs.length > 0 && (() => {
                const getLogColor = (msg: string): string => {
                  if (msg.includes('') || msg.includes('falhou') || msg.includes('failed')) return T.danger;
                  if (msg.includes('')) return T.warning;
                  if (msg.includes('') || msg.includes('concluído') || msg.includes('iniciado')) return T.success;
                  if (msg.includes('')) return T.accent;
                  if (msg.includes('') || msg.includes('')) return '#38bdf8';
                  if (msg.includes('')) return '#fb923c';
                  if (msg.includes('')) return T.textMuted;
                  return T.textSecondary;
                };
                const logFilters = [
                  { key: 'all', label: 'Todos', icon: '', match: [] as string[] },
                  { key: 'error', label: 'Erros', icon: '', match: ['', 'falhou', 'failed', 'ERRO'] },
                  { key: 'clean', label: 'Limpeza', icon: '', match: [''] },
                  { key: 'token', label: 'Tokens', icon: '', match: ['', '', 'token'] },
                  { key: 'k8s', label: 'Kubernetes', icon: '', match: ['', 'K8s', 'Pod', 'Namespace'] },
                ];
                const allLogs = selectedRequest.value.installation_logs;
                const activeFilter = logFilter.value;
                const filteredLogs = activeFilter === 'all'
                  ? allLogs
                  : allLogs.filter((l: any) => {
                    const m = typeof l === 'string' ? l : l?.message || '';
                    return logFilters.find(f => f.key === activeFilter)?.match?.some((k: string) => m.includes(k));
                  });
                return (
                <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 10, borderBottom: '1px solid #1e293b', paddingBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Logs ({allLogs.length})
                </div>
                <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
                  {logFilters.map(f => (
                    <Button variant="ghost" key={f.key} onClick={() => { logFilter.value = f.key; }} style={{
                      padding: '2px 8px', fontSize: 10, borderRadius: 4, border: 'none', cursor: 'pointer',
                      background: activeFilter === f.key ? T.textPrimary : T.bgInput,
                      color: activeFilter === f.key ? T.text : T.textMuted,
                    }}>{f.icon} {f.label}</Button>
                  ))}
                </div>
                <div style={{
                  background: T.bgInput, border: `1px solid ${T.border}`,
                  borderRadius: 8, padding: 10, fontSize: 10, fontFamily: 'monospace',
                  color: T.textSecondary, maxHeight: 400, overflowY: 'auto',
                }}>
                {filteredLogs.map((log: any, i: number) => {
                  const msg = typeof log === 'string' ? log : log?.message || '';
                  const ts = typeof log === 'object' && log?.timestamp ? new Date(log.timestamp).toLocaleTimeString('pt-BR') : '';
                  let delta = '';
                  if (i > 0) {
                    const prevLog = filteredLogs[i - 1];
                    const prevTs = typeof prevLog === 'object' ? prevLog?.timestamp : null;
                    const curTs = typeof log === 'object' ? log?.timestamp : null;
                    if (prevTs && curTs) {
                      const diffMs = new Date(curTs).getTime() - new Date(prevTs).getTime();
                      if (diffMs > 0 && diffMs < 3600000) {
                        delta = diffMs >= 60000 ? ` (+${Math.round(diffMs/60000)}m)` : ` (+${Math.round(diffMs/1000)}s)`;
                      }
                    }
                  }
                  return (
                    <div key={i} style={{ marginBottom: 4, display: 'flex', gap: 8 }}>
                      {ts && <span style={{ color: T.textMuted, minWidth: 75, flexShrink: 0 }}>{ts}{delta && <span style={{ color: T.textDisabled, fontSize: 9 }}>{delta}</span>}</span>}
                      <span style={{ color: getLogColor(msg) }}>{msg}</span>
                    </div>
                  );
                })}
                </div>
                </div>
                );
                })()}


 {/* Error */}
 {selectedRequest.value.error_message && (
 <div style={{
 background: T.dangerBg, border: `1px solid ${T.dangerBg}`,
 borderRadius: 8, padding: 12, marginBottom: 16, color: T.danger, fontSize: 11,
 fontFamily: 'monospace',
 }}>
 {selectedRequest.value.error_message}
 </div>
 )}
 </div>
 </div>
 )}
 </div>
 );
}

// ══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ══════════════════════════════════════════════════════════════════════════

function InstallHistoryModal({ machineId, machineName, onClose }: { machineId: string; machineName: string; onClose: () => void }) {
 const [data, setData] = useState<any>(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');

 useEffect(() => {
 setLoading(true);
 api.get(`/admin/machines/${machineId}/install-history`)
 .then((r: any) => { setData(r.data?.install || null); setLoading(false); })
 .catch((e: any) => { setError(e.message || 'Erro ao carregar'); setLoading(false); });
 }, [machineId]);

 const statusColor = (s: string) => s === 'completed' ? T.success : s === 'failed' ? T.danger : T.warning;
 const statusLabel = (s: string) => s === 'completed' ? 'Concluido' : s === 'failed' ? 'Erro' : s === 'processing' ? 'Em andamento' : s;
 const stepDotColor = (s: string) => s === 'completed' ? T.success : s === 'failed' ? T.danger : s === 'running' ? T.warning : T.textDisabled;

 const fmtDate = (d: string | null) => d ? new Date(d).toLocaleString('pt-BR') : '-';
 const fmtDuration = (start?: number, end?: number) => {
 if (!start || !end) return null;
 const s = Math.round((end - start) / 1000);
 return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m${s % 60}s`;
 };

 return (
 <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: T.bgOverlay, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}
 onClick={(e: any) => { if (e.target === e.currentTarget) onClose(); }}>
 <div style={{ background: T.bgInput, border: '1px solid #1e293b', borderRadius: 16, width: '90%', maxWidth: 640, maxHeight: '85vh', overflow: 'auto', padding: 28, position: 'relative' }}>
 {/* Close button */}
 <Button variant="ghost" onClick={onClose} style={{ position: 'absolute', top: 12, right: 16, background: 'none', border: 'none', color: T.textSecondary, fontSize: 22, cursor: 'pointer', fontWeight: 700 }}>X</Button>

 {loading && <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>}
 {error && <div style={{ color: T.danger, textAlign: 'center', padding: 40 }}>{error}</div>}
 {!loading && !error && !data && <div style={{ color: T.textSecondary, textAlign: 'center', padding: 40 }}>Nenhuma instalacao encontrada para esta maquina.</div>}

 {!loading && !error && data && (
 <>
 {/* Header */}
 <div style={{ marginBottom: 20 }}>
 <h2 style={{ color: '#f1f5f9', fontSize: 20, fontWeight: 700, margin: 0 }}>{machineName}</h2>
 <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
 <span style={{ background: data.type === 'reinstall' ? T.successBg : T.primaryBg, color: data.type === 'reinstall' ? T.success : T.primary, padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const }}>{data.type === 'reinstall' ? 'REINSTALL' : 'INSTALL'}</span>
 <span style={{ color: T.textMuted, fontSize: 12 }}>{fmtDate(data.createdAt)}</span>
 </div>
 </div>

 {/* Status banner */}
 <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: `${statusColor(data.status)}15`, border: `1px solid ${statusColor(data.status)}30`, marginBottom: 20 }}>
 <div style={{ width: 10, height: 10, borderRadius: '50%', background: statusColor(data.status) }} />
 <span style={{ color: statusColor(data.status), fontWeight: 600, fontSize: 13 }}>{statusLabel(data.status)}</span>
 {data.completedAt && <span style={{ color: T.textMuted, fontSize: 11, marginLeft: 'auto' }}>Finalizado: {fmtDate(data.completedAt)}</span>}
 {data.errorMessage && <span style={{ color: T.danger, fontSize: 11, marginLeft: 'auto' }}>{data.errorMessage}</span>}
 </div>

 {/* Config section */}
 {data.config && (
 <div style={{ marginBottom: 24 }}>
 <h3 style={{ color: T.textSecondary, fontSize: 12, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 1, marginBottom: 10 }}>CONFIGURACAO</h3>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
 {[
 ['Provedor', data.config.llm_provider],
 ['Modelo', data.config.llm_model],
 ['Brain', data.config.brain],
 ['Idioma', data.config.chat_language || '-'],
 ['Cliente', data.config.customer],
 ['Maquina', data.config.machine || machineName],
 ].map(([label, value]: any) => (
 <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: T.bg, borderRadius: 6 }}>
 <span style={{ color: T.textMuted, fontSize: 11 }}>{label}</span>
 <span style={{ color: T.text, fontSize: 11, fontWeight: 600 }}>{value}</span>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Steps timeline */}
 {data.steps && data.steps.length > 0 && (
 <div>
 <h3 style={{ color: T.textSecondary, fontSize: 12, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 1, marginBottom: 14 }}>Etapas do Processo</h3>
 <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
 {data.steps.map((step: any, i: number) => (
 <div key={i} style={{ display: 'flex', gap: 14, position: 'relative' }}>
 {/* Timeline line */}
 <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20 }}>
 <div style={{ width: 12, height: 12, borderRadius: '50%', background: stepDotColor(step.status), flexShrink: 0, marginTop: 2 }} />
 {i < data.steps.length - 1 && <div style={{ width: 2, flex: 1, background: T.bgInput, minHeight: 30 }} />}
 </div>
 {/* Content */}
 <div style={{ paddingBottom: 16, flex: 1 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ color: T.text, fontSize: 13, fontWeight: 600 }}>{step.name}</span>
 {step.startTime && step.endTime && (() => {
 const dur = fmtDuration(step.startTime, step.endTime);
 return dur ? <span style={{ background: T.primaryBg, color: T.primary, padding: '1px 7px', borderRadius: 4, fontSize: 10, fontWeight: 600 }}>{dur}</span> : null;
 })()}
 </div>
 <div style={{ color: T.textMuted, fontSize: 11, marginTop: 2 }}>{step.description}</div>
 {step.error && <div style={{ color: T.danger, fontSize: 11, marginTop: 4 }}>{step.error}</div>}
 </div>
 </div>
 ))}
 </div>
 </div>
 )}
 </>
 )}
 </div>
 </div>
 );
}

export function CloudAdminPage() {
 const { meta: skillsMeta } = useSkillsMeta();
 useEffect(() => { fetchAll(); }, []);

 // Cron auto-refresh
 useEffect(() => {
 let interval: any;
 const check = () => {
 if (adminTab.value === 'cron') {
 if (!interval && cronAutoRefresh.value) {
 interval = setInterval(fetchCronLogs, 10000);
 }
 } else if (interval) {
 clearInterval(interval);
 interval = null;
 }
 };
 check();
 const mainInterval = setInterval(check, 1000);
 return () => { clearInterval(mainInterval); if (interval) clearInterval(interval); };
 }, []);

 // Poll selected request detail every 10s (no flicker)
 useEffect(() => {
 const iv = setInterval(async () => {
 const req = selectedRequest.value;
 if (!req) return;
 try {
 const endpoint = req.type === 'reinstallation'
 ? `/cloud/admin/reinstallation-requests/${req.id}`
 : `/cloud/admin/installation-requests/${req.id}`;
 const fresh = await api.get<any>(endpoint);
 if (fresh && fresh.id) {
 selectedRequest.value = { ...fresh, type: req.type } as UnifiedRequest;
 }
 } catch {}
 }, 10000);
 return () => clearInterval(iv);
 }, []);

 if (loading.value) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);

 const d = dashboard.value;
 const hasDirty = Object.keys(editValues.value).length > 0;

 return (
 <div style={{ padding: 24, maxWidth: 1000 }}>
 <h1 style={{ fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 24 }}>
 Elon Cloud
 </h1>

 {error.value && (
 <div style={{ background: T.dangerBg, border: `1px solid ${T.dangerBg}`, borderRadius: 12, padding: 14, marginBottom: 16, color: T.danger, fontSize: 13 }}>
 {error.value}
 <span onClick={() => error.value = ''} style={{ float: 'right', cursor: 'pointer' }}></span>
 </div>
 )}

 {successMsg.value && (
 <div style={{ background: T.successBg, border: `1px solid ${T.successBg}`, borderRadius: 12, padding: 14, marginBottom: 16, color: T.success, fontSize: 13 }}>
 {successMsg.value}
 </div>
 )}

 {/* Stats bar */}
 {d && (
 <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
 <StatCard icon="" label="Receita" value={`$${d.revenue.total_usd.toFixed(2)}`} color={T.success} />
 <StatCard icon="" label="Máquinas" value={d.machines.total} color={T.primary} />
 <StatCard icon="" label="Running" value={d.machines.running} color={T.success} />
 <StatCard icon="" label="Assinaturas" value={d.subscriptions.active} color={T.purple} />
 </div>
 )}

 {/* Tabs */}
 <div style={{ display: 'flex', gap: 6, marginBottom: 24, background: T.bgCard, padding: 4, borderRadius: 12, flexWrap: 'nowrap', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 8 }}>
 {(['settings', 'overview', 'plans', 'machines', 'pedidos', 'agentes', 'orders', 'cron', 'backups', 'premium', 'docker', 'subscriptions'] as const).map(t => (
 <Button variant="ghost" key={t} onClick={() => {
 adminTab.value = t;
 if (t === 'pedidos' && allRequests.value.length === 0) fetchPedidos();
 if (t === 'agentes' && agentRequests.value.length === 0) fetchAgentRequests();
 if (t === 'cron') {
 fetchCronLogs();
 api.get<{ enabled: boolean }>('/cloud/admin/installation-requests/cron/status')
 .then(d => { cronEnabled.value = d.enabled; }).catch(() => {});
 }
 }} style={{
 padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer',
 background: adminTab.value === t ? T.purpleBg : 'transparent',
 color: adminTab.value === t ? T.primary : T.textSecondary,
 fontWeight: adminTab.value === t ? 700 : 500, fontSize: 14,
 whiteSpace: 'nowrap' as const, flexShrink: 0,
 }}>
 {{ settings: 'Configuracoes', overview: 'Visao Geral', plans: 'Planos', machines: 'Maquinas', pedidos: 'Pedidos', orders: 'Orders', cron: 'Cron', docker: 'Docker Images', subscriptions: 'Assinaturas' }[t]}
 </Button>
 ))}
 </div>

 {/* ═══ SETTINGS TAB ═══ */}
 {adminTab.value === 'settings' && (
 <div>
 {hasDirty && (
 <div style={{
 position: 'sticky', top: 0, zIndex: 10, padding: '12px 20px', marginBottom: 20,
 background: T.warningBg, border: `1px solid ${T.warningBg}`,
 borderRadius: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
 backdropFilter: 'blur(10px)',
 }}>
 <div style={{ fontSize: 14, fontWeight: 600, color: T.warning }}>
 {Object.keys(editValues.value).length} alteração(ões) não salva(s)
 </div>
 <div style={{ display: 'flex', gap: 10 }}>
 <Button variant="ghost" size="sm" onClick={() => editValues.value = {}}> Descartar</Button>
 <Button variant="success" size="sm" onClick={saveSettings} disabled={saving.value} loading={saving.value}> Salvar Tudo</Button>
 </div>
 </div>
 )}

 {['stripe', 'pagarme', 'hetzner', 'k8s', 'features', 'general'].map(cat => {
 const items = grouped.value[cat];
 if (!items || items.length === 0) return null;
 return <SettingsCategory key={cat} category={cat} items={items} />;
 })}
 </div>
 )}

 {/* ═══ OVERVIEW TAB ═══ */}
 {adminTab.value === 'overview' && d && (
 <div style={{
 background: T.bgInput, border: `1px solid ${T.border}`,
 borderRadius: 14, padding: 24,
 }}>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 24 }}>
 <div>
 <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 8 }}> Receita</div>
 <div style={{ fontSize: 20, fontWeight: 800, color: T.success }}>${d.revenue.total_usd.toFixed(2)}</div>
 <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{d.revenue.succeeded} OK • {d.revenue.failed} falhos</div>
 </div>
 <div>
 <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 8 }}> Máquinas</div>
 <div style={{ fontSize: 20, fontWeight: 800, color: T.purpleHover }}>{d.machines.total}</div>
 <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{d.machines.running} running • {d.machines.provisioning} instalando • {d.machines.error} erro</div>
 </div>
 <div>
 <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 8 }}> Assinaturas</div>
 <div style={{ fontSize: 20, fontWeight: 800, color: T.purple }}>{d.subscriptions.total}</div>
 <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{d.subscriptions.active} ativas • {d.subscriptions.cancelled} canceladas</div>
 </div>
 </div>
 </div>
 )}

 {/* ═══ MACHINES TAB — redesigned cards ═══ */}
 {adminTab.value === 'machines' && (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
 {/* Pool status summary */}
 {adminMachines.value.length > 0 && (() => {
 const avail = adminMachines.value.filter(m => m.pool_status === 'available').length;
 const inUse = adminMachines.value.filter(m => !m.pool_status || m.pool_status === 'in_use').length;
 const cancelled = adminMachines.value.filter(m => m.pool_status === 'cancelled').length;
 const running = adminMachines.value.filter(m => m.status === 'running').length;
 const withRunner = adminMachines.value.filter(m => m.runner_connected).length;
 const withOC = adminMachines.value.filter(m => m.openclaw_installed).length;
 return (
 <div style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 12, color: T.textMuted, flexWrap: 'wrap' }}>
 <span> Disponíveis: <b style={{ color: T.success }}>{avail}</b></span>
 <span> Em Uso: <b style={{ color: T.purpleHover }}>{inUse}</b></span>
 <span> Canceladas: <b style={{ color: T.danger }}>{cancelled}</b></span>
 <span style={{ borderLeft: `1px solid ${T.bgActive}`, paddingLeft: 16 }}> Running: <b style={{ color: T.success }}>{running}</b></span>
 <span> Runner: <b style={{ color: T.purple }}>{withRunner}</b></span>
 <span> OpenClaw: <b style={{ color: T.purpleHover }}>{withOC}</b></span>
 </div>
 );
 })()}
 {adminMachines.value.length === 0 ? (
 <div style={{ textAlign: 'center', padding: 48, color: T.textMuted }}>Nenhuma máquina encontrada.</div>
 ) : adminMachines.value.map(m => {
 const ownerEmail = m.customer_email || allCustomers.value.find(c => c.id === m.customer_id)?.email || m.customer_id.slice(0, 12) + '...';
 const statusColor = m.status === 'running' ? T.success : m.status === 'provisioning' ? T.warning : T.danger;
 const statusBg = m.status === 'running' ? T.successBg : m.status === 'provisioning' ? T.warningBg : T.dangerBg;
 const runnerColor = m.runner_connected ? T.success : T.danger;
 const runnerLabel = m.runner_connected ? 'Conectado' : 'Sem Runner';
 const ocColor = m.openclaw_installed ? T.primary : T.textMuted;
 const ocLabel = m.openclaw_installed ? 'Instalado' : 'Não';
 return (
 <div key={m.id} style={{
 background: T.bgInput, border: `1px solid ${T.border}`,
 borderRadius: 14, padding: 18, position: 'relative',
 }}>
 {/* Top row: name + badges + IP */}
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
 <div style={{ flex: 1, minWidth: 0 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
 <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}> {m.display_name || m.name}</span>
 {m.hostname && m.hostname !== (m.display_name || m.name) && (
 <span style={{ fontSize: 11, color: T.textDisabled }}>({m.hostname})</span>
 )}
 </div>
 <div style={{ fontSize: 12, color: T.textMuted, marginTop: 3 }}>
 {ownerEmail} &nbsp;•&nbsp; {m.provider}
 {m.ipv4_address && <span> &nbsp;•&nbsp; <span style={{ color: T.textSecondary, fontFamily: 'monospace', fontSize: 11 }}>{m.ipv4_address}</span></span>}
 </div>
 <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2, display: 'flex', gap: 8, alignItems: 'center' }}>
 <span>Plano: <b style={{ color: m.plan_name ? T.primary : T.danger }}>{m.plan_name || 'Sem plano'}</b></span>
 {m.sub_status && m.sub_status !== 'active' && (
 <span style={{ padding: '1px 5px', borderRadius: 10, fontSize: 9, fontWeight: 600, color: T.warning, background: T.warningBg }}>{m.sub_status}</span>
 )}
 </div>
 </div>
 {/* Status badges cluster */}
 <div style={{ display: 'flex', gap: 4, flexShrink: 1, marginLeft: 12, flexWrap: 'wrap' }}>
 <span title={`Servidor: ${m.status}`} style={{ padding: '2px 6px', borderRadius: 20, fontSize: 10, fontWeight: 600, color: statusColor, background: statusBg, whiteSpace: 'nowrap' }}>
 {m.status}
 </span>
 <span title={`Runner: ${runnerLabel}`} style={{ padding: '2px 6px', borderRadius: 20, fontSize: 10, fontWeight: 600, color: runnerColor, background: m.runner_connected ? T.successBg : T.dangerBg, whiteSpace: 'nowrap' }}>
 {runnerLabel}
 </span>
 <span title={`OpenClaw: ${ocLabel}`} style={{ padding: '2px 6px', borderRadius: 20, fontSize: 10, fontWeight: 600, color: ocColor, background: m.openclaw_installed ? T.primaryBg : T.bgHover, whiteSpace: 'nowrap' }}>
 {ocLabel}
 </span>
 </div>
 </div>

 {/* Middle row: agent, skills, setup date, installation stage */}
 <div style={{ display: 'flex', gap: 20, fontSize: 12, color: T.textMuted, flexWrap: 'wrap', marginBottom: 12 }}>
 <span title={m.agent_name || 'Sem agente'}>
 Agente: <b style={{ color: m.agent_name ? T.purple : T.textMuted }}>{m.agent_name || 'Sem agente'}</b>
 </span>
 <span title={m.skills_names?.join(', ') || 'Nenhuma skill'}>
 Skills: <b style={{ color: (m.skills_count || 0) > 0 ? T.primary : T.textMuted }}>{m.skills_count || 0}</b>
 </span>
 <span> Setup: {formatDate(m.created_at)}</span>
 {m.installation_stage && (
 <span> Stage: <b style={{ color: T.warning }}>{m.installation_stage}</b></span>
 )}
 </div>

 {/* Bottom row: actions */}
 <div style={{ display: 'flex', alignItems: 'center', gap: 6, borderTop: `1px solid ${T.bgCard}`, paddingTop: 10, flexWrap: 'wrap' }}>
 <Button variant="secondary" size="xs" onClick={() => openReassign(m)} style={{ color: T.purple, background: T.purpleBg, border: `1px solid ${T.purpleBg}` }}> Reatribuir</Button>
 <Button variant="secondary" size="xs" onClick={(e: any) => { e.stopPropagation(); reinstallMachine.value = m; showReinstall.value = true; }} style={{ color: T.warning, background: T.warningBg, border: `1px solid ${T.warningBg}` }}> Reinstalar Setup</Button>
 <Button variant="secondary" size="xs" onClick={(e: any) => { e.stopPropagation(); if (confirm('Devolver esta maquina ao pool?')) returnToPool(m.id); }} style={{ color: T.purple, background: T.purpleBg, border: `1px solid ${T.purpleBg}` }}> Devolver ao Pool</Button>
 <Button variant="secondary" size="xs" onClick={(e: any) => { e.stopPropagation(); installHistoryMachine.value = { id: m.id, name: m.display_name || m.name }; showInstallHistory.value = true; }} style={{ color: T.primary, background: T.primaryBg, border: `1px solid ${T.primaryBg}` }}> Instalacao</Button>
 <select
 value={m.pool_status || 'in_use'}
 onChange={(e: any) => changePoolStatus(m.id, e.target.value)}
 style={{
 padding: '4px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
 border: `1px solid ${T.bgActive}`, cursor: 'pointer',
 background: m.pool_status === 'available' ? T.successBg : m.pool_status === 'cancelled' ? T.dangerBg : T.purpleBg,
 color: m.pool_status === 'available' ? T.success : m.pool_status === 'cancelled' ? T.danger : T.primary,
 }}
 >
 <option value="available"> Disponível</option>
 <option value="in_use"> Em Uso</option>
 <option value="cancelled"> Cancelada</option>
 </select>
 <span style={{ marginLeft: 'auto', fontSize: 11, color: T.textDisabled, fontFamily: 'monospace' }}>{m.id.slice(0, 8)}</span>
 </div>
 </div>
 );
 })}

 {/* Install History Modal */}
 {showInstallHistory.value && installHistoryMachine.value && (
 <InstallHistoryModal
 machineId={installHistoryMachine.value.id}
 machineName={installHistoryMachine.value.name}
 onClose={() => { showInstallHistory.value = false; installHistoryMachine.value = null; }}
 />
 )}

 {/* Reassign Modal */}
 {showReassign.value && reassignMachine.value && (
 <div style={{
 position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
 background: T.bgOverlay, zIndex: 1000,
 display: 'flex', alignItems: 'center', justifyContent: 'center',
 backdropFilter: 'blur(4px)',
 }} onClick={(e: any) => { if (e.target === e.currentTarget) showReassign.value = false; }}>
 <div style={{
 background: T.bgInput, border: `1px solid ${T.purpleBg}`,
 borderRadius: 16, padding: 28, width: 460, maxHeight: '80vh', overflowY: 'auto',
 }}>
 <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 4 }}>
 Reatribuir Máquina
 </div>
 <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 20 }}>
 {reassignMachine.value.name} ({reassignMachine.value.id.slice(0, 8)}...)
 </div>

 <input
 type="text"
 placeholder="Buscar customer por email ou nome..."
 value={reassignSearch.value}
 onInput={(e: any) => reassignSearch.value = e.target.value}
 style={{
 width: '100%', padding: '10px 14px', borderRadius: 10,
 border: `1px solid ${T.bgActive}`, background: T.bgCard,
 color: T.text, fontSize: 14, outline: 'none', marginBottom: 12, boxSizing: 'border-box',
 }}
 />

 <div style={{ maxHeight: 240, overflowY: 'auto', marginBottom: 16 }}>
 {allCustomers.value
 .filter(c => {
 if (c.id === reassignMachine.value!.customer_id) return false;
 if (!reassignSearch.value) return true;
 const q = reassignSearch.value.toLowerCase();
 return (c.email?.toLowerCase().includes(q)) || (c.name?.toLowerCase().includes(q));
 })
 .map(c => (
 <div
 key={c.id}
 onClick={() => reassignCustomerId.value = c.id}
 style={{
 padding: '10px 14px', borderRadius: 10, cursor: 'pointer', marginBottom: 4,
 border: reassignCustomerId.value === c.id ? `2px solid ${T.purple}` : `1px solid ${T.border}`,
 background: reassignCustomerId.value === c.id ? T.purpleBg : T.bgCard,
 }}
 >
 <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{c.name || c.email}</div>
 <div style={{ fontSize: 11, color: T.textMuted }}>{c.email} • {c.id.slice(0, 8)}...</div>
 </div>
 ))}
 {allCustomers.value.length === 0 && (
 <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
 )}
 </div>

 {reassignCustomerId.value && (
 <div style={{
 padding: '10px 14px', borderRadius: 10, marginBottom: 16,
 background: T.warningBg, border: `1px solid ${T.warningBg}`,
 fontSize: 12, color: T.warning,
 }}>
 Tem certeza? Máquina sairá de <strong>{reassignMachine.value.customer_id.slice(0, 8)}...</strong> e entrará em{' '}
 <strong>{allCustomers.value.find(c => c.id === reassignCustomerId.value)?.email || reassignCustomerId.value.slice(0, 8)}...</strong>
 </div>
 )}

 <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
 <Button variant="ghost" onClick={() => showReassign.value = false} style={{
 padding: '10px 20px', borderRadius: 10, border: `1px solid ${T.bgActive}`,
 background: 'transparent', color: T.textSecondary, fontSize: 13, cursor: 'pointer',
 }}>
 Cancelar
 </Button>
 <Button variant="ghost"
 onClick={doReassign}
 disabled={!reassignCustomerId.value || reassigning.value}
 style={{
 padding: '10px 20px', borderRadius: 10, border: 'none',
 background: (!reassignCustomerId.value || reassigning.value) ? T.purpleBg : 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
 color: T.text, fontSize: 13, fontWeight: 700,
 cursor: (!reassignCustomerId.value || reassigning.value) ? 'not-allowed' : 'pointer',
 }}
 >
 {reassigning.value ? ' Reatribuindo...' : ' Confirmar Reatribuição'}
 </Button>
 </div>
 </div>
 </div>
 )}

 {/* Reinstall Setup Modal */}
 {showReinstall.value && reinstallMachine.value && (
 <div style={{
 position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
 background: T.bgOverlay, zIndex: 1000,
 display: 'flex', alignItems: 'center', justifyContent: 'center',
 backdropFilter: 'blur(4px)',
 }} onClick={(e: any) => { if (e.target === e.currentTarget) showReinstall.value = false; }}>
 <div style={{
 background: T.bgInput, border: `1px solid ${T.warningBg}`,
 borderRadius: 16, padding: 28, width: 500, maxHeight: '85vh', overflowY: 'auto',
 }}>
 <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 4 }}>
 Reinstalar Setup
 </div>
 <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 20 }}>
 {reinstallMachine.value.display_name || reinstallMachine.value.name} • {reinstallMachine.value.ipv4_address}
 </div>

 {[
 { label: ' Nome do Agente', key: 'agent_name', placeholder: 'Ex: Maluquinho Policy' },
 { label: ' Provedor LLM', key: 'llm_provider', placeholder: 'elon, openai, anthropic' },
 { label: ' Modelo LLM', key: 'llm_model', placeholder: 'claude-haiku-4-5, gpt-4o' },
 { label: ' Cérebro', key: 'brain', placeholder: 'clean ou ID do cérebro' },
 { label: ' Idioma', key: 'chat_language', placeholder: 'pt, en, es' },
 { label: ' Token LLM (opcional)', key: 'llm_token', placeholder: 'sk-...' },
 { label: ' Token Telegram (opcional)', key: 'telegram_token', placeholder: '123456:ABC...' },
 ].map((field: any) => (
 <div key={field.key} style={{ marginBottom: 12 }}>
 <label style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 4, display: 'block' }}>{field.label}</label>
 <input
 type="text"
 placeholder={field.placeholder}
 value={(reinstallForm.value as any)[field.key]}
 onInput={(e: any) => { reinstallForm.value = { ...reinstallForm.value, [field.key]: e.target.value }; }}
 style={{
 width: '100%', padding: '8px 12px', borderRadius: 8, fontSize: 13,
 border: `1px solid ${T.bgActive}`, background: T.bgCard,
 color: T.text, outline: 'none', boxSizing: 'border-box',
 }}
 />
 </div>
 ))}

 <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
 <Button variant="ghost" onClick={() => showReinstall.value = false} style={{
 padding: '10px 20px', borderRadius: 10, border: `1px solid ${T.bgActive}`,
 background: 'transparent', color: T.textMuted, fontSize: 13, fontWeight: 600, cursor: 'pointer',
 }}>Cancelar</Button>
 <Button variant="ghost"
 disabled={reinstallLoading.value}
 onClick={async () => {
 reinstallLoading.value = true;
 try {
 const res = await api.post('/cloud/admin/reinstallation-requests', {
 machine_id: reinstallMachine.value!.id,
 ...reinstallForm.value,
 }) as any;
 if (res?.id || res?.success) {
 showReinstall.value = false;
 reinstallForm.value = { agent_name: '', llm_provider: 'elon', llm_model: 'claude-haiku-4-5', brain: 'clean', chat_language: 'pt', llm_token: '', telegram_token: '' };
 alert(' Pedido de reinstalação criado e auto-aprovado! O cron vai processar em breve.');
 } else {
 alert(' Erro: ' + (res?.message || 'Falha ao criar pedido'));
 }
 } catch (e: any) { alert(' Erro: ' + e.message); }
 reinstallLoading.value = false;
 }}
 style={{
 padding: '10px 20px', borderRadius: 10, border: 'none',
 background: reinstallLoading.value ? T.warningBg : 'linear-gradient(135deg, #FF9500, #FF6B00)',
 color: T.text, fontSize: 13, fontWeight: 700,
 cursor: reinstallLoading.value ? 'not-allowed' : 'pointer',
 }}
 >
 {reinstallLoading.value ? ' Enviando...' : ' Reinstalar'}
 </Button>
 </div>
 </div>
 </div>
 )}
 <Pagination page={machinesPag.value.page} totalPages={machinesPag.value.totalPages} total={machinesPag.value.total} pageSize={machinesPag.value.pageSize} onPageChange={(p) => { machinesPag.value = { ...machinesPag.value, page: p }; fetchAll(); }} onPageSizeChange={(s) => { machinesPag.value = { ...machinesPag.value, pageSize: s, page: 1 }; fetchAll(); }} />
 </div>
 )}

 {/* ═══ PEDIDOS TAB ═══ */}
 {adminTab.value === 'pedidos' && <PedidosTab />}

 {adminTab.value === 'agentes' && <AgentRequestsTab />}

 {/* ═══ ORDERS TAB ═══ */}
 {adminTab.value === 'orders' && (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
 {adminOrders.value.length === 0 ? (
 <div style={{ textAlign: 'center', padding: 48, color: T.textMuted }}>Nenhum pedido encontrado.</div>
 ) : adminOrders.value.map(o => (
 <div key={o.id} style={{
 background: T.bgInput, border: `1px solid ${T.border}`,
 borderRadius: 12, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
 }}>
 <div>
 <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}> #{o.id.slice(0, 8)}</div>
 <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
 Customer: {o.customer_id.slice(0, 8)}... • Plano: {o.plan_id} • {formatDate(o.created_at)}
 </div>
 </div>
 <div style={{ textAlign: 'right' }}>
 <span style={{
 padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
 color: o.status === 'paid' ? T.success : o.status === 'pending_payment' ? T.warning : T.danger,
 background: o.status === 'paid' ? T.successBg : o.status === 'pending_payment' ? T.warningBg : T.dangerBg,
 }}>
 {o.status}
 </span>
 <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 4 }}>
 $ {o.total_price_usd.toFixed(2)}
 </div>
 </div>
 </div>
 ))}
 <Pagination page={ordersPag.value.page} totalPages={ordersPag.value.totalPages} total={ordersPag.value.total} pageSize={ordersPag.value.pageSize} onPageChange={(p) => { ordersPag.value = { ...ordersPag.value, page: p }; fetchAll(); }} onPageSizeChange={(s) => { ordersPag.value = { ...ordersPag.value, pageSize: s, page: 1 }; fetchAll(); }} />
 </div>
 )}

 {/* ═══ PLANS TAB ═══ */}
 {adminTab.value === 'plans' && (
 <div>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
 <h2 style={{ fontSize: 18, fontWeight: 700, color: T.text }}> Planos</h2>
 <Button variant="ghost" onClick={() => { if (showCreatePlan.value) { closePlanForm(); } else { editingPlan.value = null; showCreatePlan.value = true; } }} style={{
 padding: '8px 16px', borderRadius: 10, border: 'none',
 background: T.purpleBg, color: T.purpleHover, fontSize: 13, fontWeight: 600, cursor: 'pointer',
 }}>
 {showCreatePlan.value ? ' Fechar' : '+ Novo Plano'}
 </Button>
 </div>

 {showCreatePlan.value && (
 <div style={{
 background: T.bgInput, border: `1px solid ${T.primaryBg}`,
 borderRadius: 14, padding: 24, marginBottom: 16,
 }}>
 <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginBottom: 20 }}>{editingPlan.value ? ` Editar: ${editingPlan.value.name}` : 'Criar Novo Plano'}</div>

 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px', marginBottom: 16 }}>
 <InputField label="Nome do Plano *" value={newPlan.value.name} onChange={v => newPlan.value = { ...newPlan.value, name: v }} />
 <div style={{ marginBottom: 12 }}>
 <label style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, display: 'block', marginBottom: 4 }}>Provedor *</label>
 <select
 value={selectedProvider.value}
 onChange={(e: any) => {
 selectedProvider.value = e.target.value;
 newPlan.value = { ...newPlan.value, provider: e.target.value, provider_plan_id: '' };
 fetchProviderPlans(e.target.value);
 }}
 style={{
 width: '100%', padding: '10px 14px', borderRadius: 10,
 border: `1px solid ${T.bgActive}`, background: T.bgCard,
 color: T.text, fontSize: 14,
 }}
 >
 <option value="hetzner"> Hetzner Cloud</option>
 <option value="k8s">Kubernetes (DOKS)</option>
 </select>
 </div>
 </div>

 {!providerPlans.value.length && !loadingProvider.value && !providerError.value && (
 <Button variant="ghost" onClick={() => fetchProviderPlans(selectedProvider.value)} style={{
 width: '100%', padding: '14px', borderRadius: 12, border: `1px dashed ${T.primaryBgStrong}`,
 background: T.purpleBg, color: T.purpleHover, fontSize: 14, fontWeight: 600,
 cursor: 'pointer', marginBottom: 16,
 }}>
 Carregar planos do {selectedProvider.value === 'hetzner' ? 'Hetzner' : 'K8s DOKS'}
 </Button>
 )}

 {loadingProvider.value && (
 <div style={{ textAlign: 'center', padding: 20, color: T.textSecondary, fontSize: 14 }}>
 Buscando planos do {selectedProvider.value}...
 </div>
 )}

 {providerError.value && (
 <div style={{
 background: T.dangerBg, border: `1px solid ${T.dangerBg}`,
 borderRadius: 12, padding: 14, marginBottom: 16, color: T.danger, fontSize: 13,
 }}>
 {providerError.value}
 <Button variant="ghost" onClick={() => fetchProviderPlans(selectedProvider.value)} style={{
 marginLeft: 12, padding: '4px 12px', borderRadius: 6, border: 'none',
 background: T.dangerBg, color: T.danger, fontSize: 12, cursor: 'pointer',
 }}>
 Tentar novamente
 </Button>
 </div>
 )}

 {providerPlans.value.length > 0 && (
 <div style={{ marginBottom: 20 }}>
 <div style={{ fontSize: 13, fontWeight: 700, color: T.textSecondary, marginBottom: 10 }}>
 Selecione o tipo de servidor ({providerPlans.value.length} disponíveis):
 </div>
 <div style={{
 display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
 gap: 10, maxHeight: 400, overflowY: 'auto', padding: 2,
 }}>
 {providerPlans.value.map(st => {
 const isSelected = newPlan.value.provider_plan_id === st.id;
 return (
 <div
 key={st.id}
 onClick={() => selectServerType(st)}
 style={{
 padding: '14px 16px', borderRadius: 12, cursor: 'pointer',
 border: isSelected ? `2px solid ${T.primary}` : `1px solid ${T.bgHover}`,
 background: isSelected ? T.purpleBg : T.bgCard,
 transition: 'all 0.15s',
 }}
 onMouseEnter={(e: any) => { if (!isSelected) e.currentTarget.style.background = T.bgCard; }}
 onMouseLeave={(e: any) => { if (!isSelected) e.currentTarget.style.background = T.bgCard; }}
 >
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
 <span style={{ fontSize: 13, fontWeight: 700, color: isSelected ? T.primary : T.text }}>
 {st.name || st.id}
 </span>
 {isSelected && <span style={{ fontSize: 14 }}></span>}
 </div>
 <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
 <span style={{ fontSize: 11, color: T.textSecondary, background: T.bgInput, padding: '2px 8px', borderRadius: 6 }}>
 {st.cpus} vCPU
 </span>
 <span style={{ fontSize: 11, color: T.textSecondary, background: T.bgInput, padding: '2px 8px', borderRadius: 6 }}>
 {st.memory_gb} GB
 </span>
 <span style={{ fontSize: 11, color: T.textSecondary, background: T.bgInput, padding: '2px 8px', borderRadius: 6 }}>
 {st.storage_gb} GB {st.storage_type || ''}
 </span>
 </div>
 {(st.price_monthly_eur || st.price_monthly_usd || st.price_monthly_cents) && (
 <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>
 Custo: {st.price_monthly_cents
 ? `R$${(st.price_monthly_cents / 100).toFixed(2)}/mês`
 : st.price_monthly_eur
 ? `€${st.price_monthly_eur.toFixed(2)}/mês`
 : `$${st.price_monthly_usd?.toFixed(2)}/mês`}
 {st.first_period_cents && (
 <span style={{ color: T.purpleHover, marginLeft: 6 }}>
 {`(1º: R$${(st.first_period_cents / 100).toFixed(2)})`}
 </span>
 )}
 </div>
 )}
 {st.cpu_type && (
 <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>
 {st.cpu_type} {st.architecture ? `• ${st.architecture}` : ''}
 </div>
 )}
 </div>
 );
 })}
 </div>
 </div>
 )}

 {newPlan.value.provider_plan_id && (
 <div style={{
 background: T.purpleBg, border: `1px solid ${T.primaryBg}`,
 borderRadius: 12, padding: 16, marginBottom: 16,
 }}>
 <div style={{ fontSize: 13, fontWeight: 700, color: T.purpleHover, marginBottom: 8 }}>
 Servidor selecionado: <code style={{ background: T.borderHover, padding: '2px 8px', borderRadius: 6 }}>{newPlan.value.provider_plan_id}</code>
 </div>
 <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, color: T.textSecondary }}>
 <span> {newPlan.value.cpus} vCPUs</span>
 <span> {newPlan.value.memory_gb} GB RAM</span>
 <span> {newPlan.value.storage_gb} GB SSD</span>
 </div>
 </div>
 )}

 <div style={{ fontSize: 13, fontWeight: 700, color: T.textSecondary, marginBottom: 10 }}> Preço de Venda</div>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px', marginBottom: 12 }}>
 <InputField label="Preço USD (mensal) *" value={newPlan.value.price_usd} onChange={v => newPlan.value = { ...newPlan.value, price_usd: v }} type="number" />
 <InputField label="Preço BRL (mensal)" value={newPlan.value.price_brl} onChange={v => newPlan.value = { ...newPlan.value, price_brl: v }} type="number" />
 </div>
 <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px', marginBottom: 12 }}>
 <InputField label="Bandwidth (TB)" value={newPlan.value.bandwidth_gb} onChange={v => newPlan.value = { ...newPlan.value, bandwidth_gb: v }} type="number" />
 <InputField label="Max Agentes" value={newPlan.value.max_agents} onChange={v => newPlan.value = { ...newPlan.value, max_agents: v }} type="number" />
 <InputField label="Bonus Credits ($)" value={newPlan.value.bonus_credits} onChange={v => newPlan.value = { ...newPlan.value, bonus_credits: v }} type="number" />
 </div>
 <InputField label="Descrição" value={newPlan.value.description} onChange={v => newPlan.value = { ...newPlan.value, description: v }} />

 <Button variant="ghost"
 onClick={createPlan}
 disabled={creating.value || !newPlan.value.name || !newPlan.value.price_usd}
 style={{
 width: '100%', padding: '14px 24px', borderRadius: 12, border: 'none', marginTop: 8,
 background: (creating.value || !newPlan.value.name) ? T.primaryBg : 'linear-gradient(135deg, #2F6BFF, #8B5CF6)',
 color: T.text, fontSize: 15, fontWeight: 700,
 cursor: (creating.value || !newPlan.value.name) ? 'not-allowed' : 'pointer',
 }}
 >
 {creating.value ? ' Salvando...' : editingPlan.value ? ' Salvar Alterações' : ' Criar Plano'}
 </Button>
 </div>
 )}

 <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
 {plans.value.map(p => (
 <div key={p.id} style={{
 background: T.bgInput, border: `1px solid ${T.border}`,
 borderRadius: 12, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
 opacity: p.active ? 1 : 0.5,
 }}>
 <div style={{ flex: 1 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
 <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{p.name}</span>
 <span style={{
 padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
 background: p.active ? T.successBg : T.dangerBg,
 color: p.active ? T.success : T.danger,
 }}>
 {p.active ? 'Ativo' : 'Inativo'}
 </span>
 </div>
 <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>
 {p.cpus} vCPU • {p.memory_gb} GB RAM • {p.storage_gb} GB SSD • {p.provider}
 {p.max_agents ? ` • ${p.max_agents} agentes` : ''}
 {p.bonus_credits ? ` • $${p.bonus_credits} credits` : ''}
 </div>
 </div>
 <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
 <div style={{ textAlign: 'right' }}>
 <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>${p.price_usd}</div>
 {p.price_brl && <div style={{ fontSize: 11, color: T.textMuted }}>R${p.price_brl}</div>}
 </div>
 <Button variant="secondary" size="xs" onClick={() => openPlanEdit(p)} style={{ background: T.purpleBg, color: T.purpleHover }}> Editar</Button>
 <Button variant="ghost" size="xs" onClick={() => togglePlan(p.id, p.active)} style={{ color: p.active ? T.danger : T.success }}>
 {p.active ? 'Desativar' : 'Ativar'}
 </Button>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* ═══ CEREBROS TAB ═══ */}
 {/* ═══ DOCS TAB ═══ */}
 {/* DocsTab moved to /admin/docs */}

 {/* ═══ CRON TAB ═══ */}
 {adminTab.value === 'cron' && (
 <div>
 {/* Cron Status Card */}
 <div style={{
 background: cronEnabled.value ? T.successBg : T.dangerBg,
 border: `1px solid ${cronEnabled.value ? T.successBg : T.dangerBg}`,
 borderRadius: 14, padding: 20, marginBottom: 20,
 display: 'flex', justifyContent: 'space-between', alignItems: 'center',
 }}>
 <div>
 <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>
 {cronEnabled.value ? ' Cron Job ATIVO' : ' Cron Job INATIVO'}
 </div>
 <div style={{ fontSize: 13, color: T.textSecondary, marginTop: 4 }}>
 {cronEnabled.value ? `Processando pedidos automaticamente a cada ${cronInterval.value} minutos` : 'Nenhum processamento automático'}
 </div>
 <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
 Frequência: 
 <input
 type="number"
 min={1}
 max={60}
 value={cronInterval.value}
 onChange={async (e) => {
 const val = parseInt((e.target as HTMLInputElement).value);
 if (val >= 1 && val <= 60) {
 cronInterval.value = val;
 try {
 await api.put('/cloud/admin/installation-requests/cron/interval', { minutes: val });
 } catch {}
 }
 }}
 style={{
 width: 50, padding: '4px 8px', borderRadius: 6, border: `1px solid ${T.borderHover}`,
 background: T.bgInput, color: T.text, fontSize: 12, textAlign: 'center',
 }}
 /> min • Limite: 3 pedidos/ciclo (instalação) + 3 (reinstalação)
 </div>
 </div>
 <Button variant="ghost" onClick={handleCronToggle} style={{
 padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700,
 background: cronEnabled.value ? T.dangerBg : T.successBg,
 color: cronEnabled.value ? T.dangerHover : T.success, fontSize: 14,
 }}>
 {cronEnabled.value ? ' Desativar' : ' Ativar'}
 </Button>
 </div>

 {/* Stats Row */}
 <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
 <div style={{ flex: 1, background: T.bgInput, borderRadius: 12, padding: 16, border: `1px solid ${T.border}` }}>
 <div style={{ fontSize: 11, color: T.textMuted }}>Última Execução</div>
 <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 4 }}>
 {cronLogs.value.length > 0 ? formatDate(cronLogs.value[0].started_at) : '—'}
 </div>
 </div>
 <div style={{ flex: 1, background: T.bgInput, borderRadius: 12, padding: 16, border: `1px solid ${T.border}` }}>
 <div style={{ fontSize: 11, color: T.textMuted }}>Processados (total)</div>
 <div style={{ fontSize: 14, fontWeight: 700, color: T.purpleHover, marginTop: 4 }}>
 {cronLogs.value.reduce((sum, l) => sum + l.processed, 0)}
 </div>
 </div>
 <div style={{ flex: 1, background: T.bgInput, borderRadius: 12, padding: 16, border: `1px solid ${T.border}` }}>
 <div style={{ fontSize: 11, color: T.textMuted }}>Sucesso</div>
 <div style={{ fontSize: 14, fontWeight: 700, color: T.success, marginTop: 4 }}>
 {cronLogs.value.reduce((sum, l) => sum + l.succeeded, 0)}
 </div>
 </div>
 <div style={{ flex: 1, background: T.bgInput, borderRadius: 12, padding: 16, border: `1px solid ${T.border}` }}>
 <div style={{ fontSize: 11, color: T.textMuted }}>Falhas</div>
 <div style={{ fontSize: 14, fontWeight: 700, color: T.dangerHover, marginTop: 4 }}>
 {cronLogs.value.reduce((sum, l) => sum + l.failed, 0)}
 </div>
 </div>
 </div>

 {/* Refresh controls */}
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
 <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}> Log de Execuções</div>
 <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
 <label style={{ fontSize: 12, color: T.textSecondary, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
 <input type="checkbox" checked={cronAutoRefresh.value} onChange={() => cronAutoRefresh.value = !cronAutoRefresh.value} />
 Auto-refresh (10s)
 </label>
 <Button variant="ghost" onClick={fetchCronLogs} style={{
 padding: '6px 14px', borderRadius: 8, border: `1px solid ${T.bgActive}`,
 background: 'transparent', color: T.textSecondary, fontSize: 12, cursor: 'pointer',
 }}>
 Atualizar
 </Button>
 </div>
 </div>

 {/* Logs list */}
 {loadingCronLogs.value ? (
 <div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>
 ) : cronLogs.value.length === 0 ? (
 <div style={{ textAlign: 'center', padding: 48, color: T.textMuted }}>
 Nenhuma execução registrada ainda. O cron roda a cada 5 minutos.
 </div>
 ) : (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
 {cronLogs.value.map(log => {
 const statusColors: Record<string, string> = {
 running: T.primary, success: T.success, partial: T.warning, skipped: T.textMuted, error: T.dangerHover,
 };
 const statusLabels: Record<string, string> = {
 running: ' Rodando', success: ' Sucesso', partial: ' Parcial', skipped: ' Pulado', error: ' Erro',
 };
 const sc = statusColors[log.status] || T.textMuted;
 const duration = log.completed_at
 ? `${((new Date(log.completed_at).getTime() - new Date(log.started_at).getTime()) / 1000).toFixed(1)}s`
 : 'em andamento...';

 return (
 <div key={log.id} style={{
 background: T.bgInput, border: `1px solid ${sc}30`,
 borderRadius: 12, padding: 16,
 }}>
 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
 <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
 <span style={{ padding: '3px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, background: sc + '20', color: sc }}>
 {statusLabels[log.status] || log.status}
 </span>
 <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>
 {formatDate(log.started_at)}
 </span>
 <span style={{ fontSize: 11, color: T.textMuted }}>
 {duration}
 </span>
 </div>
 <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
 {log.processed > 0 && <span style={{ color: T.purpleHover }}> {log.processed}</span>}
 {log.succeeded > 0 && <span style={{ color: T.success }}> {log.succeeded}</span>}
 {log.failed > 0 && <span style={{ color: T.dangerHover }}> {log.failed}</span>}
 {log.no_machines > 0 && <span style={{ color: T.warning }}> {log.no_machines} sem VM</span>}
 </div>
 </div>

 {log.details && log.details.length > 0 && (
 <div style={{ marginTop: 8, padding: '8px 12px', background: T.bgOverlay, borderRadius: 8 }}>
 {log.details.map((d: string, i: number) => (
 <div key={i} style={{ fontSize: 11, color: T.textSecondary, padding: '2px 0', fontFamily: 'monospace' }}>
 {d}
 </div>
 ))}
 </div>
 )}

 {log.error_message && (
 <div style={{ marginTop: 8, fontSize: 11, color: T.dangerHover, background: T.dangerBg, padding: '6px 10px', borderRadius: 6, fontFamily: 'monospace' }}>
 {log.error_message}
 </div>
 )}
 </div>
 );
 })}
 </div>
 )}
 </div>
 )}

 {adminTab.value === 'backups' && <AdminBackupsTab />}
 {adminTab.value === 'premium' && <AdminPremiumTab />}
 {adminTab.value === 'docker' && <AdminDockerImagesPage />}
      {adminTab.value === 'subscriptions' && <SubscriptionsTab />}
      {/* Servers tab moved to standalone /admin/servers page */}
      {adminTab.value === 'pool' && <K8sClusterTab />}
      {adminTab.value === 'costs' && <CostsTab />}
      {adminTab.value === 'installations' && <InstallationsTab />}

 </div>
 );
}

// ═══ K8S CLUSTER TAB ═══
function K8sClusterTab() {
 const [clusterData, setClusterData] = useState<any>(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');

 useEffect(() => {
   fetchCluster();
   const iv = setInterval(fetchCluster, 30000);
   return () => clearInterval(iv);
 }, []);

 const fetchCluster = async () => {
   setLoading(true);
   setError('');
   try {
     const res = await api.get<any>('/admin/cluster/metrics');
     setClusterData(res);
   } catch (e: any) {
     setError(e.message || 'Erro ao carregar cluster');
   } finally {
     setLoading(false);
   }
 };

 if (loading && !clusterData) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);
 if (error) return <div style={{ color: T.danger, padding: 20 }}>{error} <Button variant="ghost" onClick={fetchCluster} style={{ marginLeft: 12, padding: '6px 14px', borderRadius: 8, border: `1px solid ${T.bgActive}`, background: T.bgInput, color: T.text, cursor: 'pointer' }}>Tentar novamente</Button></div>;
 if (!clusterData) return null;

 const cluster = clusterData.cluster || {};
 const nodes = clusterData.nodes || clusterData.nodePools?.flatMap((p: any) => p.nodes) || [];
 const workspaces = clusterData.workspaces || [];

 const kpiItems = [
   { label: 'Nodes', value: cluster.nodeCount || nodes.length, color: T.primary, icon: '' },
   { label: 'Nodes Ready', value: cluster.readyNodes || nodes.filter((n: any) => n.status === 'running').length, color: T.success, icon: '' },
   { label: 'Workspaces', value: workspaces.length || cluster.workspaceCount || 0, color: T.purple, icon: '' },
   { label: 'Pods Running', value: clusterData.podsRunning || 0, color: T.success, icon: '' },
   { label: 'CPU Usado', value: `${(clusterData.cpuUsagePercent || 0).toFixed(0)}%`, color: T.warning, icon: '' },
   { label: 'RAM Usado', value: `${(clusterData.memoryUsagePercent || 0).toFixed(0)}%`, color: T.danger, icon: '' },
 ];

 const statusBadge = (status: string) => {
   const colors: Record<string, string> = { running: T.success, Ready: T.success, NotReady: T.danger, provisioning: T.warning };
   return <span style={{ padding: '2px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: `${colors[status] || T.textMuted}22`, color: colors[status] || T.textSecondary }}>{status}</span>;
 };

 return (
   <div>
     {/* KPIs */}
     <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
       {kpiItems.map(ki => (
         <div key={ki.label} style={{ flex: '1 1 140px', background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px 20px', textAlign: 'center' }}>
           <div style={{ fontSize: 24 }}>{ki.icon}</div>
           <div style={{ fontSize: 28, fontWeight: 700, color: ki.color }}>{typeof ki.value === 'number' ? ki.value : ki.value}</div>
           <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>{ki.label}</div>
         </div>
       ))}
     </div>

     {/* Cluster info */}
     {cluster.name && (
       <div style={{ background: T.primaryBg, border: `1px solid ${T.primaryBg}`, borderRadius: 14, padding: 16, marginBottom: 20, display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 13, color: T.textSecondary }}>
         <span><strong style={{ color: T.text }}>{cluster.name}</strong></span>
         <span>Regiao: {cluster.region}</span>
         <span>Versao: {cluster.version}</span>
         <span>Status: {statusBadge(cluster.status || 'running')}</span>
       </div>
     )}

     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
       <h3 style={{ margin: 0, color: T.text }}>Nodes do Cluster ({nodes.length})</h3>
       <Button variant="ghost" onClick={fetchCluster} style={{ padding: '8px 16px', borderRadius: 8, border: `1px solid ${T.bgActive}`, background: T.bgInput, color: T.text, cursor: 'pointer', fontSize: 13 }}>
         {loading ? 'Atualizando...' : 'Atualizar'}
       </Button>
     </div>

     {/* Node cards */}
     <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
       {nodes.map((n: any, i: number) => (
         <div key={n.id || n.name || i} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20 }}>
           <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
             <span style={{ fontWeight: 700, color: T.text, fontSize: 15 }}>{n.name || `Node ${i + 1}`}</span>
             {statusBadge(n.status || 'running')}
             {n.size && <span style={{ color: T.purple, fontSize: 13, fontWeight: 600 }}>{n.size}</span>}
           </div>
           <div style={{ display: 'flex', gap: 20, fontSize: 13, color: T.textSecondary, marginBottom: 8 }}>
             {n.cpu != null && <span>CPU: {typeof n.cpu === 'number' ? `${n.cpu.toFixed(0)}%` : n.cpu}</span>}
             {n.memory != null && <span>RAM: {typeof n.memory === 'number' ? `${n.memory.toFixed(0)}%` : n.memory}</span>}
             {n.disk != null && <span>Disco: {typeof n.disk === 'number' ? `${n.disk.toFixed(0)}%` : n.disk}</span>}
           </div>
           <div style={{ display: 'flex', gap: 20, fontSize: 12, color: T.textMuted }}>
             {n.dropletId && <span>Droplet: {n.dropletId}</span>}
             {n.createdAt && <span>Criado: {new Date(n.createdAt).toLocaleDateString('pt-BR')}</span>}
           </div>
         </div>
       ))}
       {nodes.length === 0 && <div style={{ color: T.textMuted, textAlign: 'center', padding: 40 }}>Nenhum node encontrado no cluster</div>}
     </div>
   </div>
 );
}

// ═══ ADMIN BACKUPS TAB ═══
function AdminBackupsTab() {
 const [requests, setRequests] = useState<any[]>([]);
 const [autoConfigs, setAutoConfigs] = useState<any[]>([]);
 const [loading, setLoading] = useState(true);
 const [processing, setProcessing] = useState(false);

 useEffect(() => {
 fetchData();
 }, []);

 const fetchData = async () => {
 try {
 const [reqRes, autoRes] = await Promise.all([
 api.get<{ requests: any[] }>('/cloud/admin/backup-requests').catch(() => ({ requests: [] })),
 api.get<{ configs: any[] }>('/cloud/admin/auto-backups').catch(() => ({ configs: [] })),
 ]);
 setRequests(reqRes.requests || []);
 setAutoConfigs(autoRes.configs || []);
 } catch {}
 setLoading(false);
 };

 const runProcessor = async () => {
 setProcessing(true);
 try {
 const res = await api.post<{ processed: number; results: any[] }>('/cloud/backup-processor/run', {});
 showToast(`Processados: ${res.processed} backups`, 'success');
 fetchData();
 } catch (e: any) {
 showToast(e?.message || 'Erro', 'error');
 }
 setProcessing(false);
 };

 const runAutoBackups = async () => {
 setProcessing(true);
 try {
 const res = await api.post<{ created: number; skipped: number }>('/cloud/backup-processor/auto-run', {});
 showToast(`Criados: ${res.created}, Pulados: ${res.skipped}`, 'success');
 fetchData();
 } catch (e: any) {
 showToast(e?.message || 'Erro', 'error');
 }
 setProcessing(false);
 };

 if (loading) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);

 const statusColor: Record<string, string> = { pending: T.warning, processing: T.primary, completed: T.success, failed: T.danger };

 return (
 <div>
 {/* Actions */}
 <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
 <Button variant="ghost" onClick={runProcessor} disabled={processing} style={{
 padding: '10px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700,
 background: T.purpleBg, border: `1px solid ${T.primaryBg}`,
 color: T.purpleHover, cursor: processing ? 'not-allowed' : 'pointer',
 }}>{processing ? 'Processando...' : 'Processar Pendentes'}</Button>
 <Button variant="ghost" onClick={runAutoBackups} disabled={processing} style={{
 padding: '10px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700,
 background: T.successBg, border: `1px solid ${T.successBg}`,
 color: T.success, cursor: processing ? 'not-allowed' : 'pointer',
 }}>{processing ? '...' : 'Gerar Auto-Backups'}</Button>
 </div>

 {/* Auto-backup configs */}
 {autoConfigs.length > 0 && (
 <div style={{ marginBottom: 24 }}>
 <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 10 }}>Backup Automático ({autoConfigs.length})</h3>
 <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
 {autoConfigs.map((c: any) => (
 <div key={c.id} style={{
 padding: '10px 14px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10,
 background: T.bgCard, border: `1px solid ${T.border}`,
 }}>
 <div style={{ width: 6, height: 6, borderRadius: 3, background: c.enabled ? T.success : T.textMuted }} />
 <span style={{ fontSize: 12, color: T.textSecondary, flex: 1 }}>
 Customer: {c.customer_id?.slice(0, 8)}... | Machine: {c.machine_id?.slice(0, 8)}... | {c.backup_type} | {c.schedule}
 </span>
 <span style={{ fontSize: 10, color: c.enabled ? T.success : T.textMuted, fontWeight: 600 }}>
 {c.enabled ? 'ATIVO' : 'INATIVO'}
 </span>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Backup requests */}
 <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 10 }}>Pedidos de Backup ({requests.length})</h3>
 {requests.length === 0 ? (
 <div style={{ textAlign: 'center', padding: 30, color: T.textMuted, fontSize: 13 }}>Nenhum pedido</div>
 ) : (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
 {requests.map((r: any) => (
 <div key={r.id} style={{
 padding: '10px 14px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10,
 background: T.bgCard, border: `1px solid ${T.border}`,
 }}>
 <span style={{
 padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
 background: `${statusColor[r.status] || T.textMuted}15`,
 color: statusColor[r.status] || T.textMuted,
 }}>{r.status}</span>
 <span style={{ fontSize: 12, color: T.textSecondary, flex: 1 }}>
 {r.backup_type} | Customer: {r.customer_id?.slice(0, 8)}... | Runner: {r.runner_id?.slice(0, 8)}...
 </span>
 {r.file_size ? <span style={{ fontSize: 10, color: T.textMuted }}>{(r.file_size / 1024).toFixed(0)}KB</span> : null}
 {r.error_message && <span style={{ fontSize: 10, color: T.danger, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.error_message}</span>}
 <span style={{ fontSize: 10, color: T.textMuted }}>{new Date(r.created_at).toLocaleDateString('pt-BR')}</span>
 </div>
 ))}
 </div>
 )}
 </div>
 );
}

// ═══ ADMIN SUBSCRIPTIONS TAB (Assinaturas + Máquinas) ═══
function SubscriptionsTab() {
  const [subs, setSubs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [pag, setPag] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 0 });

  const fetchSubs = async (page = 1, pageSize = 50) => {
    setLoading(true);
    try {
      const data = await api.get<any>(`/cloud/admin/subscriptions?page=${page}&pageSize=${pageSize}`);
      setSubs(data.subscriptions || []);
      if (data.pagination) setPag(data.pagination);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchSubs(); }, []);

  if (loading) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);

  const statusColor: Record<string, string> = { active: T.success, canceled: T.danger, cancelled: T.danger, expired: T.warning, past_due: T.warning, replaced: T.textMuted };
  const statusLabel: Record<string, string> = { active: 'Ativo', canceled: 'Cancelado', cancelled: 'Cancelado', expired: 'Expirado', past_due: 'Vencido', replaced: 'Substituído' };

  const fmtTokens = (n: number) => {
    if (!n) return '0';
    if (n < 1000) return String(n);
    if (n < 1_000_000) return `${Math.round(n / 1000)}k`;
    return `${(n / 1_000_000).toFixed(1)}m`;
  };

  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

  const copyId = (id: string) => { navigator.clipboard.writeText(id).catch(() => {}); };

  return (
    <div>
 <h3 style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16 }}>Assinaturas ({pag.total || subs.length})</h3>
      {subs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: T.textMuted, fontSize: 13 }}>Nenhuma assinatura encontrada</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {subs.map((s: any) => {
            const p = s.plan || {};
            const u = s.llm_usage || {};
            return (
            <div key={s.id} style={{
              padding: '16px 18px', borderRadius: 12,
              background: T.bgCard, border: `1px solid ${T.border}`,
            }}>
              {/* Header row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                <span style={{
                  padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                  background: `${statusColor[s.status] || T.textMuted}20`,
                  color: statusColor[s.status] || T.textMuted,
                }}>{statusLabel[s.status] || s.status}</span>
                <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>
                  {s.customer_email || s.customer_name || s.customer_id?.slice(0, 12)}
                </span>
                <span style={{
                  padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                  background: T.purpleBg, color: T.purple,
                }}>{p.name || s.plan_id}</span>
                <span
                  onClick={() => copyId(s.id)}
                  title="Clique para copiar ID completo"
                  style={{ fontSize: 10, color: T.textMuted, fontFamily: 'monospace', cursor: 'pointer', borderBottom: '1px dashed rgba(100,116,139,0.4)' }}
                >{s.id?.slice(0, 8)}</span>
 {s.auto_renew && <span style={{ fontSize: 10, color: T.success }}>Auto</span>}
              </div>

              {/* Info row */}
              <div style={{ fontSize: 11, color: T.textSecondary, marginBottom: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <span>Início: {fmtDate(s.started_at)}</span>
                <span>Renovação: {fmtDate(s.next_billing_date)}</span>
                {s.ended_at && <span>Encerrado: {fmtDate(s.ended_at)}</span>}
                <span>Ciclos: {s.cycles_completed ?? 0}</span>
              </div>

              {/* Plan details row */}
              <div style={{
                fontSize: 10, color: T.textMuted, marginBottom: 10, display: 'flex', gap: 12, flexWrap: 'wrap',
                padding: '6px 10px', borderRadius: 6, background: T.bgCard,
              }}>
 <span> {p.cpus || '?'} vCPU</span>
 <span> {p.memory_gb || '?'}GB RAM</span>
 <span> {p.storage_gb || '?'}GB Disk</span>
 <span> {p.bandwidth_gb || '?'}GB BW</span>
 <span> ${p.price_usd || 0} / R${p.price_brl || 0}</span>
 {p.provider && <span> {p.provider}</span>}
              </div>

              {/* Payment info */}
              {s.payment && (
                <div style={{
                  fontSize: 10, color: T.textMuted, marginBottom: 10, display: 'flex', gap: 12, flexWrap: 'wrap',
                  padding: '6px 10px', borderRadius: 6, background: T.successBg,
                }}>
 <span>Pago: <b style={{ color: s.payment.paid_usd > 0 ? T.success : T.textMuted }}>${s.payment.paid_usd?.toFixed(2) || '0.00'}</b>{s.payment.paid_brl ? ` / R$${s.payment.paid_brl?.toFixed(2)}` : ''}</span>
 {s.payment.gateway && <span> {s.payment.gateway}</span>}
 {s.payment.method && <span> {s.payment.method}</span>}
 {s.payment.paid_at && <span>Pago em: {new Date(s.payment.paid_at).toLocaleDateString('pt-BR')}</span>}
 {s.payment.order_status && <span style={{ color: s.payment.order_status === 'paid' || s.payment.order_status === 'active' ? T.success : T.warning }}> {s.payment.order_status}</span>}
 {!s.payment.paid_at && s.payment.paid_usd === 0 && <span style={{ color: T.warning }}>Gratuito / Não pago</span>}
                </div>
              )}

              {/* Machines section */}
              {s.machines && s.machines.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                  {s.machines.map((m: any) => (
                    <div key={m.id} style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 11,
                      background: T.purpleBg, border: `1px solid ${T.purpleBg}`,
                      color: T.text,
                    }}>
 {m.name || m.id?.slice(0, 8)} {m.ip && `• ${m.ip}`}
 {m.agent_name && ` • ${m.agent_name}`}
                      <span style={{
                        marginLeft: 6, fontSize: 9, fontWeight: 700,
                        color: m.status === 'running' ? T.success : m.status === 'failed' ? T.danger : T.warning,
                      }}>{m.status}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 11, color: T.textMuted, fontStyle: 'italic', marginBottom: 8 }}>Sem máquina vinculada</div>
              )}

              {/* LLM Usage section */}
              {(u.total_tokens > 0 || u.total_messages > 0) && (
                <div style={{
                  fontSize: 10, color: T.textSecondary, display: 'flex', gap: 14,
                  padding: '5px 10px', borderRadius: 6, background: T.successBg, border: `1px solid ${T.successBg}`,
                }}>
 <span>Tokens: {fmtTokens(u.total_tokens)}</span>
 <span>Msgs: {u.total_messages || 0}</span>
 <span>Custo: ${(u.total_cost || 0).toFixed(4)}</span>
                </div>
              )}
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

// ═══ ADMIN PREMIUM TAB ═══
function AdminPremiumTab() {
 const [subs, setSubs] = useState<any[]>([]);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 (async () => {
 try {
 const res = await api.get<{ subscriptions: any[] }>('/cloud/admin/subscriptions');
 setSubs(res.subscriptions || []);
 } catch {}
 setLoading(false);
 })();
 }, []);

 if (loading) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);

 const statusColor: Record<string, string> = { active: T.success, canceled: T.warning, expired: T.danger, past_due: T.danger, replaced: T.textMuted };

 const activeCount = subs.filter(s => s.status === 'active').length;
 const totalRevenue = subs.filter(s => s.status === 'active').reduce((sum, s) => {
 return sum + (s.plan === 'premium_yearly' ? 34.99 : 4.99);
 }, 0);

 return (
 <div>
 {/* Stats */}
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
 <div style={{ padding: '16px', borderRadius: 10, background: T.bgInput, border: `1px solid ${T.border}` }}>
 <div style={{ fontSize: 24, fontWeight: 800, color: T.text }}>{subs.length}</div>
 <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600 }}>Total</div>
 </div>
 <div style={{ padding: '16px', borderRadius: 10, background: T.bgInput, border: `1px solid ${T.border}` }}>
 <div style={{ fontSize: 24, fontWeight: 800, color: T.success }}>{activeCount}</div>
 <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600 }}>Ativos</div>
 </div>
 <div style={{ padding: '16px', borderRadius: 10, background: T.bgInput, border: `1px solid ${T.border}` }}>
 <div style={{ fontSize: 24, fontWeight: 800, color: T.purpleHover }}>${totalRevenue.toFixed(2)}</div>
 <div style={{ fontSize: 11, color: T.textMuted, fontWeight: 600 }}>MRR</div>
 </div>
 </div>

 {/* Subscriptions list */}
 <h3 style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 10 }}>Assinaturas ({subs.length})</h3>
 {subs.length === 0 ? (
 <div style={{ textAlign: 'center', padding: 30, color: T.textMuted, fontSize: 13 }}>Nenhuma assinatura</div>
 ) : (
 <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
 {subs.map((s: any) => (
 <div key={s.id} style={{
 padding: '12px 14px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10,
 background: T.bgCard, border: `1px solid ${T.border}`,
 }}>
 <span style={{
 padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 700,
 background: `${statusColor[s.status] || T.textMuted}15`,
 color: statusColor[s.status] || T.textMuted,
 }}>{s.status}</span>
 <div style={{ flex: 1 }}>
 <span style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>
 {s.customer_name || s.email || s.customer_id?.slice(0, 12) + '...'}
 </span>
 <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 8 }}>
 {s.plan === 'premium_yearly' ? 'Anual $34.99' : 'Mensal $4.99'}
 </span>
 </div>
 {s.current_period_end && (
 <span style={{ fontSize: 10, color: T.textMuted }}>
 {s.cancel_at_period_end ? 'Expira' : 'Renova'}: {new Date(s.current_period_end).toLocaleDateString('pt-BR')}
 </span>
 )}
 <span style={{ fontSize: 10, color: T.textMuted }}>{new Date(s.created_at).toLocaleDateString('pt-BR')}</span>
 </div>
 ))}
 </div>
 )}
 </div>
 );
}

function CostsTab() {
  const [costs, setCosts] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState('');

  const fetchCosts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/cloud/admin/costs');
      setCosts(res.costs || []);
      setSummary(res.summary || null);
    } catch (e: any) {
      setMsg('Erro ao carregar custos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCosts(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    setMsg('');
    try {
      const res = await api.post('/cloud/admin/costs/sync');
      setMsg(res.message || 'Sincronizado');
      await fetchCosts();
    } catch (e: any) {
      setMsg('Erro ao sincronizar: ' + (e.message || 'desconhecido'));
    } finally {
      setSyncing(false);
    }
  };

  const fmt = (cents: number) => `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, color: T.text, fontSize: 18 }}>Custos Infraestrutura (Valores Reais)</h3>
        <Button variant="ghost"
          onClick={handleSync}
          disabled={syncing}
          style={{
            background: syncing ? T.textPrimary : T.primary,
            color: T.text,
            border: 'none',
            borderRadius: 8,
            padding: '8px 16px',
            cursor: syncing ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {syncing ? 'Sincronizando...' : 'Atualizar Custos'}
        </Button>
      </div>

      {msg && (
        <div style={{
          background: msg.includes('Erro') ? T.dangerBg : T.successBg,
          color: msg.includes('Erro') ? T.danger : T.success,
          padding: '8px 12px',
          borderRadius: 8,
          fontSize: 13,
        }}>
          {msg}
        </div>
      )}

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
          {[
            { label: 'Total Mensal', value: summary.total_monthly_brl ? `R$ ${Number(summary.total_monthly_brl).toFixed(2).replace('.', ',')}` : 'R$ 0,00', accent: true },
            { label: 'Servidores Ativos', value: summary.active_servers || 0 },
            { label: 'Total Servidores', value: summary.total_servers || 0 },
            { label: 'Ultima Sync', value: summary.last_synced ? new Date(summary.last_synced).toLocaleString('pt-BR') : 'Nunca' },
          ].map((card) => (
            <div key={card.label} style={{
              background: T.bgInput,
              border: '1px solid #1e293b',
              borderRadius: 10,
              padding: 16,
              textAlign: 'center',
            }}>
              <div style={{ color: T.textSecondary, fontSize: 12, marginBottom: 4 }}>{card.label}</div>
              <div style={{ color: (card as any).accent ? T.success : T.text, fontSize: 20, fontWeight: 700 }}>{card.value}</div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 16 }}><SkeletonBar width="50%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="30%" height={14} /></div>
      ) : costs.length === 0 ? (
        <div style={{ color: T.textSecondary, textAlign: 'center', padding: 32 }}>
          Nenhum custo registrado. Clique "Atualizar Custos" para sincronizar.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1e293b' }}>
                {['Servidor', 'Plano', 'Specs', 'Valor Pago', 'Renovacao', 'Status', 'Proximo Billing'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: T.textSecondary, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {costs.map((row: any) => (
                <tr key={row.id} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '8px 12px' }}>
                    <div style={{ color: T.text, fontWeight: 500 }}>{row.hostname || '-'}</div>
                    {row.ip && <div style={{ color: T.textMuted, fontSize: 11 }}>{row.ip}</div>}
                  </td>
                  <td style={{ padding: '8px 12px', color: T.textSecondary }}>{row.plan_name || '-'}</td>
                  <td style={{ padding: '8px 12px', color: T.textMuted, fontSize: 12 }}>
                    {row.cpus && row.memory_mb ? `${row.cpus}vCPU / ${(row.memory_mb / 1024).toFixed(0)}GB` : '-'}
                    {row.disk_mb ? ` / ${(row.disk_mb / 1024).toFixed(0)}GB` : ''}
                  </td>
                  <td style={{ padding: '8px 12px', color: T.success, fontWeight: 600 }}>{fmt(row.amount_cents || 0)}/mes</td>
                  <td style={{ padding: '8px 12px', color: T.textSecondary, fontSize: 12 }}>{fmt(row.renewal_cents || 0)}/mes</td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{
                      background: row.status === 'active' ? T.successBg : T.dangerBg,
                      color: row.status === 'active' ? T.success : T.danger,
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                    }}>
                      {row.status || 'unknown'}
                    </span>
                    {row.state && row.state !== row.status && (
                      <span style={{ color: T.textMuted, fontSize: 11, marginLeft: 4 }}>({row.state})</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px', color: T.textSecondary, fontSize: 12 }}>
                    {row.next_billing_at ? new Date(row.next_billing_at).toLocaleDateString('pt-BR') : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pagination page={pag.page} totalPages={pag.totalPages} total={pag.total} pageSize={pag.pageSize} onPageChange={(p) => fetchSubs(p, pag.pageSize)} onPageSizeChange={(s) => fetchSubs(1, s)} />
    </div>
  );
}

// ═══ INSTALLATIONS TAB ═══
function InstallationsTab() {
  const [data, setData] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [pag, setPag] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 0 });

  const fetchInstalls = (page = 1, pageSize = 50) => {
    setLoading(true);
    api.get<any>(`/cloud/admin/installations?page=${page}&pageSize=${pageSize}`).then((res: any) => {
      setData(res.installations || []);
      setSummary(res.summary || {});
      if (res.pagination) setPag(res.pagination);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchInstalls(); }, []);

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filtered = data.filter(r => {
    // Type filter
    if (typeFilter === 'install' && r.type !== 'install') return false;
    if (typeFilter === 'reinstall' && r.type !== 'reinstall') return false;
    // Status filter
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    return true;
  });

  const statusColor = (s: string): string => {
    const m: Record<string, string> = { pending: T.textMuted, approved: T.warning, processing: T.primary, completed: T.success, failed: T.danger, cancelled: T.warning, rejected: T.dangerHover };
    return m[s] || T.textMuted;
  };
  const statusLabel = (s: string): string => {
    const m: Record<string, string> = { pending: 'Pendente', approved: 'Aprovado', processing: 'Processando', completed: 'Concluido', failed: 'Falhou', cancelled: 'Cancelado', rejected: 'Rejeitado' };
    return m[s] || s;
  };

  const parseLogs = (raw: any): any[] => {
    if (!raw) return [];
    try { return JSON.parse(raw); } catch { return []; }
  };

  if (loading) return (<div style={{ padding: 16 }}>
          {[1,2,3,4,5].map(i => (
            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${T.bgCard}` }}>
              <SkeletonBar width="20%" height={14} />
              <SkeletonBar width="30%" height={14} />
              <SkeletonBar width="25%" height={14} />
              <SkeletonBar width="15%" height={14} />
            </div>
          ))}
        </div>);

  const typeBtns = [
    { key: 'all', label: 'Todas' },
    { key: 'install', label: 'Instalacoes' },
    { key: 'reinstall', label: 'Reinstalacoes' },
  ];
  const statusBtns = [
    { key: 'all', label: 'Todos' },
    { key: 'pending', label: 'Pendentes' },
    { key: 'approved', label: 'Aprovados' },
    { key: 'processing', label: 'Processando' },
    { key: 'completed', label: 'Concluidas' },
    { key: 'failed', label: 'Falhas' },
    { key: 'cancelled', label: 'Canceladas' },
    { key: 'rejected', label: 'Rejeitados' },
  ];

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total', value: data.length, color: T.textSecondary },
          { label: 'Pendentes', value: data.filter(r => r.status === 'pending').length, color: T.textMuted },
          { label: 'Aprovados', value: data.filter(r => r.status === 'approved').length, color: T.warning },
          { label: 'Processando', value: data.filter(r => r.status === 'processing').length, color: T.primary },
          { label: 'Concluidas', value: data.filter(r => r.status === 'completed').length, color: T.success },
          { label: 'Falhas', value: data.filter(r => r.status === 'failed').length, color: T.danger },
          { label: 'Canceladas', value: data.filter(r => r.status === 'cancelled').length, color: T.warning },
          { label: 'Rejeitados', value: data.filter(r => r.status === 'rejected').length, color: T.dangerHover },
        ].map(c => (
          <div key={c.label} style={{ background: T.bgInput, border: '1px solid #1e293b', borderRadius: 12, padding: '12px 16px', minWidth: 90, flex: '1 1 90px' }}>
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{c.label}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      {/* Type filter */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Tipo</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {typeBtns.map(f => (
            <Button variant="ghost" key={f.key} onClick={() => setTypeFilter(f.key)} style={{
              padding: '6px 14px', borderRadius: 8, border: '1px solid #1e293b', cursor: 'pointer',
              background: typeFilter === f.key ? T.primaryBg : T.bgInput,
              color: typeFilter === f.key ? T.primary : T.textSecondary,
              fontWeight: typeFilter === f.key ? 600 : 400, fontSize: 13,
            }}>{f.label}</Button>
          ))}
        </div>
      </div>

      {/* Status filter */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>Status</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {statusBtns.map(f => (
            <Button variant="ghost" key={f.key} onClick={() => setStatusFilter(f.key)} style={{
              padding: '6px 14px', borderRadius: 8, border: '1px solid #1e293b', cursor: 'pointer',
              background: statusFilter === f.key ? T.purpleBg : T.bgInput,
              color: statusFilter === f.key ? T.purple : T.textSecondary,
              fontWeight: statusFilter === f.key ? 600 : 400, fontSize: 13,
            }}>{f.label}</Button>
          ))}
        </div>
      </div>

      {/* Installation cards */}
      {filtered.length === 0 ? (
        <div style={{ color: T.textMuted, textAlign: 'center', padding: 40 }}>Nenhuma instalacao encontrada</div>
      ) : filtered.map((r: any) => {
        const uid = `${r.type}-${r.id}`;
        const isOpen = expanded.has(uid);
        const logs = parseLogs(r.installation_logs);
        return (
          <div key={uid} style={{ background: T.bgInput, border: '1px solid #1e293b', borderRadius: 12, padding: 16, marginBottom: 12 }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{
                padding: '2px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                background: r.type === 'install' ? T.primaryBg : T.purpleBg,
                color: r.type === 'install' ? T.primary : T.purple,
              }}>{r.type === 'install' ? 'INSTALL' : 'REINSTALL'}</span>
              <span style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{r.display_name || r.machine_name || r.machine_id || '—'}</span>
              <span style={{ fontSize: 12, color: T.textMuted }}>{r.created_at ? new Date(r.created_at).toLocaleString('pt-BR') : ''}</span>
              <span style={{
                marginLeft: 'auto', padding: '2px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: `${statusColor(r.status)}22`, color: statusColor(r.status),
              }}>{statusLabel(r.status)}</span>
            </div>

            {/* Info row */}
            <div style={{ display: 'flex', gap: 16, fontSize: 13, color: T.textSecondary, marginBottom: 6, flexWrap: 'wrap' }}>
              {r.agent_name && <span>Agente: {r.agent_name}</span>}
              {r.llm_provider && <span>LLM: {r.llm_provider}{r.llm_model ? `/${r.llm_model}` : ''}</span>}
              {r.brain_id && <span>Brain: {r.brain_id}</span>}
              {r.ipv4_address && <span>IP: {r.ipv4_address}</span>}
            </div>

            {/* Customer email */}
            {r.customer_email && <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 8 }}>{r.customer_email}</div>}

            {/* Progress bar */}
            {r.progress != null && r.progress > 0 && r.progress < 100 && (
              <div style={{ background: T.bgInput, borderRadius: 4, height: 6, marginBottom: 8, overflow: 'hidden' }}>
                <div style={{ width: `${r.progress}%`, height: '100%', background: T.warning, borderRadius: 4, transition: 'width 0.3s' }} />
              </div>
            )}

            {/* Expand logs button */}
            <Button variant="ghost" onClick={() => toggleExpand(uid)} style={{
              background: 'none', border: '1px solid #1e293b', borderRadius: 6, padding: '4px 12px',
              color: T.textSecondary, fontSize: 12, cursor: 'pointer',
            }}>{isOpen ? 'Fechar Logs' : 'Ver Logs'}</Button>

            {/* Expanded logs */}
            {isOpen && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #1e293b' }}>
                {r.installation_stage && (
                  <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 8 }}>Stage atual: <strong style={{ color: T.text }}>{r.installation_stage}</strong></div>
                )}
                {r.progress != null && (
                  <div style={{ fontSize: 13, color: T.textSecondary, marginBottom: 8 }}>Progresso: <strong style={{ color: T.text }}>{r.progress}%</strong></div>
                )}
                {(r.error_message || r.last_error) && (
                  <div style={{ fontSize: 13, color: T.danger, marginBottom: 8, padding: 8, background: T.dangerBg, borderRadius: 6 }}>
                    {r.error_message || r.last_error}
                  </div>
                )}
                {logs.length > 0 ? (
                  <div style={{ marginTop: 8 }}>
                    {logs.map((step: any, i: number) => (
                      <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 0', borderLeft: '2px solid #1e293b', marginLeft: 8, paddingLeft: 16 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: step.ok !== false ? T.success : T.danger, marginTop: 4, flexShrink: 0 }} />
                        <div>
                          <div style={{ fontSize: 13, color: T.text, fontWeight: 500 }}>{step.message || step.stage || `Step ${i + 1}`}</div>
                          <div style={{ fontSize: 11, color: T.textMuted }}>{step.timestamp || ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: T.textMuted }}>Sem logs disponiveis</div>
                )}
                {/* Timestamps */}
                <div style={{ display: 'flex', gap: 16, fontSize: 11, color: T.textMuted, marginTop: 12, flexWrap: 'wrap' }}>
                  {r.created_at && <span>Criado: {new Date(r.created_at).toLocaleString('pt-BR')}</span>}
                  {r.started_at && <span>Iniciado: {new Date(r.started_at).toLocaleString('pt-BR')}</span>}
                  {r.completed_at && <span>Concluido: {new Date(r.completed_at).toLocaleString('pt-BR')}</span>}
                </div>
              </div>
            )}
          </div>
        );
      })}
      <Pagination page={pag.page} totalPages={pag.totalPages} total={pag.total} pageSize={pag.pageSize} onPageChange={(p) => fetchInstalls(p, pag.pageSize)} onPageSizeChange={(s) => fetchInstalls(1, s)} />
    </div>
  );
}
